import React, { useState, useEffect, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";

import {
  Plane, Hotel, UtensilsCrossed, Ticket, ShoppingBag, HeartPulse,
  Sparkles, FileText, Shield, MoreHorizontal, MapPin, Calendar,
  TrendingUp, LogOut, Settings, Plus, ArrowLeft, RefreshCw,
  ExternalLink, Trash2, User, Lock, Globe, ChevronDown, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle, X, BarChart3, Loader2, Home,
  CreditCard, DollarSign, Landmark, MessageCircle, Send, Download,
  Zap, ShoppingCart, Utensils, Car, Clapperboard, BookOpen, Package, Wallet
} from "lucide-react";

const SUPABASE_URL = "https://scxfudifnuknobvurnox.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNjeGZ1ZGlmbnVrbm9idnVybm94Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4NTc4MzIsImV4cCI6MjA4ODQzMzgzMn0.FXvM8IWsMz9eDgCccH01EghEctirz_ZtCwazVAieZfo";
const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const GOOGLE_CLIENT_ID = "650592599147-bekm0cafiqvfjn3tfa2rvo32ri18hu78.apps.googleusercontent.com";
const EDGE_FN_URL = "https://scxfudifnuknobvurnox.supabase.co/functions/v1/google-sheets";
const EDGE_HEADERS = {
  "Content-Type": "application/json",
  "Authorization": "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNjeGZ1ZGlmbnVrbm9idnVybm94Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI4NTc4MzIsImV4cCI6MjA4ODQzMzgzMn0.FXvM8IWsMz9eDgCccH01EghEctirz_ZtCwazVAieZfo",
};

const CURRENCIES = [
  "USD","EUR","GBP","SGD","JPY","AUD","CAD","CHF","HKD",
  "MYR","THB","IDR","INR","KRW","CNY","NZD","SEK","NOK",
  "DKK","PHP","VND","TWD","BRL","MXN","ZAR","AED","SAR"
];

const CATEGORIES = [
  { value: "food",          label: "Food",         Icon: UtensilsCrossed },
  { value: "accommodation", label: "Stay",         Icon: Hotel },
  { value: "transport",     label: "Transport",    Icon: Plane },
  { value: "activities",    label: "Activities",   Icon: Ticket },
  { value: "shopping",      label: "Shopping",     Icon: ShoppingBag },
  { value: "health",        label: "Health",       Icon: HeartPulse },
  { value: "beauty",        label: "Beauty",       Icon: Sparkles },
  { value: "visa",          label: "Visa",         Icon: FileText },
  { value: "insurance",     label: "Insurance",    Icon: Shield },
  { value: "other",         label: "Other",        Icon: MoreHorizontal },
];

const CAT_COLORS = {
  food:          "#fdecea", accommodation: "#fdf0e6", transport: "#fdf5dc",
  activities:    "#edf6eb", shopping:      "#e4f5f2", health:    "#e6f3fa",
  beauty:        "#e6eaf8", visa:          "#f0ecfa", insurance: "#f7edf4", other: "#fdeef0",
};
const CAT_TEXT = {
  food:          "#d96b5a", accommodation: "#e8935a", transport: "#d4a832",
  activities:    "#7aab72", shopping:      "#2eaa96", health:    "#48a0cc",
  beauty:        "#4060c0", visa:          "#9b84cc", insurance: "#a05c8a", other: "#e07b8a",
};

const TRIP_COLOURS = [
  { id:"coral",  bg:"#d96b5a", light:"#fdecea", label:"Coral"  },
  { id:"peach",  bg:"#e8935a", light:"#fdf0e6", label:"Peach"  },
  { id:"gold",   bg:"#d4a832", light:"#fdf5dc", label:"Gold"   },
  { id:"sage",   bg:"#7aab72", light:"#edf6eb", label:"Sage"   },
  { id:"teal",   bg:"#2eaa96", light:"#e4f5f2", label:"Teal"   },
  { id:"sky",    bg:"#48a0cc", light:"#e6f3fa", label:"Sky"    },
  { id:"cobalt", bg:"#4060c0", light:"#e6eaf8", label:"Cobalt" },
  { id:"lilac",  bg:"#9b84cc", light:"#f0ecfa", label:"Lilac"  },
  { id:"plum",   bg:"#a05c8a", light:"#f7edf4", label:"Plum"   },
  { id:"rose",   bg:"#e07b8a", light:"#fdeef0", label:"Rose"   },
];

function getTripColour(trip) {
  return TRIP_COLOURS.find(c => c.id === trip.colour) || TRIP_COLOURS[0];
}

const HOME_CATEGORIES = [
  { value: "housing",       label: "Housing",       Icon: Home },
  { value: "utilities",     label: "Utilities",     Icon: Zap },
  { value: "groceries",     label: "Groceries",     Icon: ShoppingCart },
  { value: "dining",        label: "Dining",        Icon: Utensils },
  { value: "transport",     label: "Transport",     Icon: Car },
  { value: "health",        label: "Health",        Icon: HeartPulse },
  { value: "personal_care", label: "Personal Care", Icon: Sparkles },
  { value: "entertainment", label: "Fun", Icon: Clapperboard },
  { value: "shopping",      label: "Shopping",      Icon: ShoppingBag },
  { value: "other",         label: "Other",         Icon: Package },
];

const HOME_CAT_COLORS = {
  housing:       "#fdecea", utilities:     "#fdf0e6", groceries:     "#fdf5dc",
  dining:        "#edf6eb", transport:     "#e4f5f2", health:        "#e6f3fa",
  personal_care: "#e6eaf8", entertainment: "#f0ecfa", shopping:      "#f7edf4", other: "#fdeef0",
};
const HOME_CAT_TEXT = {
  housing:       "#d96b5a", utilities:     "#e8935a", groceries:     "#d4a832",
  dining:        "#7aab72", transport:     "#2eaa96", health:        "#48a0cc",
  personal_care: "#4060c0", entertainment: "#9b84cc", shopping:      "#a05c8a", other: "#e07b8a",
};

function getTripStatus(trip) {
  const today = new Date(); today.setHours(0,0,0,0);
  const start = new Date(trip.startDate + "T00:00:00");
  const end   = new Date(trip.endDate   + "T00:00:00");
  if (today < start) return "upcoming";
  if (today > end)   return "past";
  return "active";
}

const STATUS_STYLE = {
  upcoming: { bg:"#eff6ff", color:"#1d4ed8", label:"Upcoming" },
  active:   { bg:"#f0fdf4", color:"#15803d", label:"Active"   },
  past:     { bg:"#f4f4f5", color:"#71717a", label:"Past"     },
};

function uid() { return Math.random().toString(36).slice(2, 10); }
function genCode(name = "") {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const suffix = () => Array.from({length: 3}, () => chars[Math.floor(Math.random() * chars.length)]).join("");
  const base = name.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);
  return base ? base + suffix() : Array.from({length: 8}, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

const ZERO_DP_CURRENCIES = new Set(["JPY","KRW","IDR","VND","KWD","BIF","CLP","GNF","ISK","MGA","PYG","RWF","UGX","XAF","XOF","XPF"]);

function currencyDp(currency) {
  return ZERO_DP_CURRENCIES.has(currency) ? 0 : 2;
}

function fmt(amount, currency, decimals) {
  const dp = decimals !== undefined ? decimals : currencyDp(currency);
  try {
    return new Intl.NumberFormat("en", {
      style: "currency", currency,
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(amount);
  } catch {
    return currency + " " + amount.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
  }
}
function fmtCompact(amount, currency) {
  return fmt(amount, currency);
}

const S = {
  app:   { minHeight: "100vh", fontFamily: "'DM Sans', system-ui, sans-serif", background: "#f8f7f4", color: "#1a1714" },
  card:  { background: "white", border: "1px solid #e5e0d8", borderRadius: "14px", boxShadow: "0 1px 8px rgba(0,0,0,0.06)" },
  input: { width: "100%", border: "1.5px solid #e5e0d8", borderRadius: "10px", padding: "9px 12px", fontSize: "16px", fontFamily: "'DM Sans', inherit", outline: "none", background: "#faf9f7", color: "#1a1714", boxSizing: "border-box", transition: "border-color .15s", minWidth: 0, height: "41px", appearance: "none", WebkitAppearance: "none" },
  label: { display: "block", fontSize: "11px", fontWeight: 600, color: "#7a7060", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "5px" },
  btnPrimary:   { display:"flex", alignItems:"center", justifyContent:"center", gap:"6px", padding:"10px 18px", background:"#1a1714", color:"#f5f0e8", border:"none", borderRadius:"10px", fontSize:"13px", fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"background .15s" },
  btnSecondary: { display:"flex", alignItems:"center", justifyContent:"center", gap:"6px", padding:"9px 14px", background:"white", color:"#504940", border:"1.5px solid #e5e0d8", borderRadius:"10px", fontSize:"13px", cursor:"pointer", fontFamily:"inherit" },
  btnGhost:     { display:"flex", alignItems:"center", gap:"5px", padding:"6px 10px", background:"transparent", border:"none", borderRadius:"8px", fontSize:"13px", cursor:"pointer", color:"#625a4d", fontFamily:"inherit" },
  btnDanger:    { display:"flex", alignItems:"center", justifyContent:"center", gap:"6px", padding:"9px 14px", background:"#fef2f2", color:"#dc2626", border:"1px solid #fecaca", borderRadius:"10px", fontSize:"13px", cursor:"pointer", fontFamily:"inherit" },
  overlay: { position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px", zIndex:50 },
  topbar: { background:"rgba(248,247,244,0.92)", borderBottom:"1px solid #e5e0d8", padding:"0 12px", height:"56px", display:"flex", alignItems:"center", gap:"8px", position:"sticky", top:0, zIndex:20, backdropFilter:"blur(10px)" },
};

function useRates(baseCurrency) {
  const [rates, setRates]   = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchRates = useCallback(async (base) => {
    if (!base) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`https://api.frankfurter.dev/v1/latest?base=${base}`);
      if (!res.ok) throw new Error("Rate fetch failed");
      const data = await res.json();
      setRates({ ...data.rates, [base]: 1 });
      setLastUpdated(new Date());
    } catch (e) {
      setError("Could not fetch live rates. Using fallback estimates.");
      const fallback = {
        USD:1, EUR:1.170, GBP:1.350, SGD:1.272, JPY:157.0, AUD:0.673,
        CAD:0.928, CHF:1.130, HKD:7.787, MYR:4.470, THB:31.6, IDR:16880,
        INR:90.1, KRW:1430, CNY:6.983, NZD:0.578, SEK:0.096, NOK:0.094,
        DKK:0.145, PHP:59.2, VND:25400, TWD:32.8, BRL:5.85, MXN:17.96,
        ZAR:18.3, AED:3.673, SAR:3.751,
      };
      const baseVal = fallback[base] || 1;
      const converted = {};
      for (const [k, v] of Object.entries(fallback)) converted[k] = v / baseVal;
      setRates(converted);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (baseCurrency) fetchRates(baseCurrency); }, [baseCurrency, fetchRates]);

  function convert(amount, fromCurrency) {
    if (!fromCurrency || fromCurrency === baseCurrency) return amount;
    if (!rates[fromCurrency]) return null;
    return amount / rates[fromCurrency];
  }

  return { rates, loading, error, lastUpdated, refresh: () => fetchRates(baseCurrency), convert };
}

export default function App() {
  const [user,           setUser]           = useState(null);
  const [screen,         setScreen]         = useState("login");
  const [mode,           setMode]           = useState(() => localStorage.getItem("sejour_mode") || "travel");
  const [monthlyExpenses,   setMonthlyExpenses]   = useState([]);
  const [allMonthlyData,    setAllMonthlyData]    = useState({}); // keyed by "YYYY-MM"
  const [monthlyBudgets,    setMonthlyBudgets]    = useState({}); // keyed by "YYYY-MM"
  const [homeView,          setHomeView]          = useState("landing"); // "landing" | "detail"
  const [currentMonth,      setCurrentMonth]      = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [tripId,         setTripId]         = useState(null);
  const [toast,          setToast]          = useState(null);
  const [openNewTrip,    setOpenNewTrip]    = useState(false);
  const [firstTrip,      setFirstTrip]      = useState(false);
  const [prevScreen,     setPrevScreen]     = useState("dashboard");
  const [appLoading,     setAppLoading]     = useState(true);

  function switchMode(m) {
    setMode(m);
    localStorage.setItem("sejour_mode", m);
  }

  async function loadFullUser(userId) {
    const { data: u } = await supabase.from("users").select("*").eq("id", userId).single();
    if (!u) return null;
    const { data: trips } = await supabase.from("trips").select("*").eq("user_id", userId).order("created_at", { ascending: false });
    const tripList = await Promise.all((trips || []).map(async t => {
      const { data: expenses } = await supabase.from("expenses").select("*").eq("trip_id", t.id).order("date", { ascending: false });
      return dbTripToApp(t, expenses || []);
    }));
    return dbUserToApp(u, tripList);
  }

  async function loadMonthlyExpenses(userId, year, month) {
    const start  = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const end    = new Date(year, month + 1, 0);
    const endStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(end.getDate()).padStart(2, "0")}`;
    const { data } = await supabase.from("monthly_expenses")
      .select("*").eq("user_id", userId)
      .gte("date", start).lte("date", endStr)
      .order("date", { ascending: false });
    return (data || []).map(e => ({ id: e.id, category: e.category, description: e.description || "", amount: e.amount, date: e.date, notes: e.notes || "" }));
  }

  async function loadMonthlyBudget(userId, year, month) {
    const { data } = await supabase.from("monthly_budgets")
      .select("budget").eq("user_id", userId).eq("year", year).eq("month", month).single();
    return data ? parseFloat(data.budget) : null;
  }

  async function loadAllMonthlyBudgets(userId) {
    const { data } = await supabase.from("monthly_budgets").select("year,month,budget").eq("user_id", userId);
    const byMonth = {};
    (data || []).forEach(r => {
      const key = `${r.year}-${String(r.month+1).padStart(2,"0")}`;
      byMonth[key] = parseFloat(r.budget);
    });
    return byMonth;
  }

  async function loadAllMonthlyData(userId) {
    // Load last 12 months of data for charts
    const now   = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const startStr = `${start.getFullYear()}-${String(start.getMonth()+1).padStart(2,"0")}-01`;
    const { data } = await supabase.from("monthly_expenses")
      .select("*").eq("user_id", userId).gte("date", startStr)
      .order("date", { ascending: true });
    const byMonth = {};
    (data || []).forEach(e => {
      const key = e.date.slice(0, 7); // "YYYY-MM"
      if (!byMonth[key]) byMonth[key] = [];
      byMonth[key].push({ id: e.id, category: e.category, description: e.description || "", amount: e.amount, date: e.date, notes: e.notes || "" });
    });
    return byMonth;
  }

  function dbUserToApp(u, trips = []) {
    return { id: u.id, accessCode: u.access_code, displayName: u.display_name, homeCurrency: u.home_currency, hasGoogle: u.has_google || false, spreadsheetId: u.spreadsheet_id || null, email: u.email || "", monthlyBudget: u.monthly_budget || "", trips };
  }

  function dbTripToApp(t, expenses = []) {
    return {
      id: t.id, name: t.name, destination: t.destination,
      startDate: t.start_date, endDate: t.end_date,
      currency: t.currency, budget: t.budget || "",
      colour: t.colour || "teal", synced: t.synced || false,
      expenses: expenses.map(e => ({ id: e.id, category: e.category, description: e.description, amount: e.amount, currency: e.currency, date: e.date, notes: e.notes || "" })),
    };
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthCode  = urlParams.get("code");
    const oauthState = urlParams.get("state");

    if (oauthCode && oauthState) {
      window.history.replaceState({}, document.title, window.location.pathname);
      const savedUserId = localStorage.getItem("sejour_user_id");
      fetch(EDGE_FN_URL + "/callback", {
        method: "POST",
        headers: EDGE_HEADERS,
        body: JSON.stringify({ code: oauthCode, state: oauthState }),
      }).then(async (res) => {
        const data = await res.json();
        if (data.success && savedUserId) {
          const u = await loadFullUser(savedUserId);
          if (u) { setUser(u); setScreen("dashboard"); showToast("Google Sheets connected!"); }
        }
        setAppLoading(false);
      });
      return;
    }

    const saved      = localStorage.getItem("sejour_user_id");
    const loginAt    = localStorage.getItem("sejour_login_at");
    const SESSION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
    const expired    = !loginAt || (Date.now() - parseInt(loginAt, 10)) > SESSION_MS;
    if (saved && !expired) {
      loadFullUser(saved).then(async u => {
        if (u) {
          setUser(u);
          const savedMode = localStorage.getItem("sejour_mode") || "travel";
          setMode(savedMode);
          if (savedMode === "home") {
            const now = new Date();
            const yr = now.getFullYear(), mo = now.getMonth();
            setCurrentMonth({ year: yr, month: mo });
            const exps = await loadMonthlyExpenses(u.id, yr, mo);
            setMonthlyExpenses(exps);
            loadAllMonthlyData(u.id).then(setAllMonthlyData);
            loadAllMonthlyBudgets(u.id).then(setMonthlyBudgets);
            setScreen("home-dashboard");
          } else {
            setScreen("dashboard");
          }
        }
        setAppLoading(false);
      });
    } else {
      if (expired && saved) {
        localStorage.removeItem("sejour_user_id");
        localStorage.removeItem("sejour_login_at");
      }
      setAppLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes spin { to { transform: rotate(360deg); } }
      *, *::before, *::after { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; overflow-x: hidden; width: 100%; max-width: 100vw; }
      body { font-family: 'DM Sans', system-ui, sans-serif; }
      input, select, textarea {
        min-width: 0; width: 100%; max-width: 100%;
        box-sizing: border-box; font-size: 16px;
      }
      input, select {
        height: 41px !important;
        -webkit-appearance: none; appearance: none;
        line-height: normal;
      }
      input[type="date"] {
        display: flex; align-items: center;
      }
      .g2 { display: grid; grid-template-columns: 1fr; gap: 10px; }
      .g3 { display: grid; grid-template-columns: 1fr; gap: 8px; }
      @media (min-width: 540px) {
        .g2 { grid-template-columns: 1fr 1fr; }
        .g3 { grid-template-columns: 1fr 1fr 1fr; }
      }
      @media (max-width: 539px) {
        .desktop-only { display: none !important; }
        .stats-grid   { grid-template-columns: 1fr 1fr !important; }
        .trip-layout  { grid-template-columns: 1fr !important; }
        .topbar-mobile-gap { gap: 4px !important; }
        .topbar-btn-sm { padding: 5px 7px !important; }
        input[type="date"] { font-size: 16px !important; padding: 8px 6px !important; -webkit-appearance: none; appearance: none; }
        select { font-size: 16px !important; padding: 8px 8px !important; -webkit-appearance: none; appearance: none; }
        .modal-sheet {
          width: calc(100vw - 32px) !important; max-width: 480px !important;
          max-height: calc(100vh - 32px) !important;
          border-radius: 16px !important; padding: 0 !important; margin: 0 !important;
        }
      }
      @media (min-width: 540px) {
        .desktop-only { display: flex !important; }
        .trip-layout  { grid-template-columns: 1fr 250px !important; }
        .modal-sheet { border-radius: 14px !important; max-height: 90vh !important; margin: auto !important; }
      }
    `;
    document.head.appendChild(style);

    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.href = "https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&family=DM+Mono:wght@300;400&display=swap";
    document.head.appendChild(font);

    if (!document.querySelector('meta[name="viewport"]')) {
      const meta = document.createElement("meta");
      meta.name = "viewport";
      meta.content = "width=device-width, initial-scale=1, viewport-fit=cover";
      document.head.appendChild(meta);
    }

    return () => { document.head.removeChild(style); };
  }, []);

  function showToast(msg, type = "success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  }

  function updateUser(fn) { setUser(prev => fn(prev)); }
  function updateTrips(fn) { setUser(prev => ({ ...prev, trips: fn(prev.trips) })); }

  const currentTrip = user && tripId ? user.trips.find(t => t.id === tripId) : null;

  if (appLoading) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", background:"#faf9f6" }}>
        <div style={{ textAlign:"center" }}>
          <Loader2 size={32} color="#a39888" style={{ animation:"spin 1s linear infinite", marginBottom:12 }} />
          <p style={{ color:"#a39888", fontSize:"13px" }}>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.app}>
      {toast && (
        <div style={{
          position:"fixed", bottom:20, left:"50%", transform:"translateX(-50%)",
          background: toast.type === "error" ? "#dc2626" : "#1a1714",
          color:"#f5f0e8", padding:"10px 18px", borderRadius:"999px",
          fontSize:"13px", fontWeight:500, zIndex:99, whiteSpace:"nowrap",
          display:"flex", alignItems:"center", gap:"8px",
          boxShadow:"0 4px 16px rgba(0,0,0,0.2)"
        }}>
          {toast.type === "error" ? <AlertCircle size={14} /> : <CheckCircle size={14} />}
          {toast.msg}
        </div>
      )}
      {screen === "login" && (
        <LoginScreen onLogin={(u, newTrip, isNew) => {
          setUser(u);
          if (isNew) {
            setScreen("mode-pick");
          } else {
            const savedMode = localStorage.getItem("sejour_mode") || "travel";
            setMode(savedMode);
            if (savedMode === "home") {
              loadMonthlyExpenses(u.id, currentMonth.year, currentMonth.month).then(setMonthlyExpenses);
              loadAllMonthlyData(u.id).then(setAllMonthlyData);
              loadAllMonthlyBudgets(u.id).then(setMonthlyBudgets);
              setScreen("home-dashboard");
            } else {
              setOpenNewTrip(!!newTrip);
              setScreen("dashboard");
              if (!newTrip) showToast("Welcome back, " + u.displayName + "!");
            }
          }
        }} />
      )}
      {screen === "mode-pick" && user && (
        <ModePickScreen
          user={user}
          onPick={(m) => {
            switchMode(m);
            if (m === "home") {
              loadMonthlyExpenses(user.id, currentMonth.year, currentMonth.month).then(setMonthlyExpenses);
              setScreen("home-onboarding");
            } else {
              setScreen("travel-onboarding");
            }
          }}
        />
      )}
      {screen === "travel-onboarding" && user && (
        <TravelOnboardingScreen
          user={user}
          onAddTrip={() => { setOpenNewTrip(true); setScreen("dashboard"); }}
          onSkip={() => setScreen("dashboard")}
        />
      )}
      {screen === "home-onboarding" && user && (
        <HomeOnboardingScreen
          user={user}
          updateUser={updateUser}
          onDone={() => {
            loadMonthlyExpenses(user.id, currentMonth.year, currentMonth.month).then(setMonthlyExpenses);
            setScreen("home-dashboard");
            showToast("Welcome, " + user.displayName + "!");
          }}
        />
      )}
      {screen === "dashboard" && user && (
        <Dashboard user={user} updateUser={updateUser} updateTrips={updateTrips}
          mode={mode} switchMode={(m) => {
            switchMode(m);
            if (m === "home") {
              loadMonthlyExpenses(user.id, currentMonth.year, currentMonth.month).then(setMonthlyExpenses);
              setScreen("home-dashboard");
            }
          }}
          onOpenTrip={(id, isFirst) => { setTripId(id); setFirstTrip(!!isFirst); setScreen("trip"); }}
          onLogout={() => { localStorage.removeItem("sejour_user_id"); localStorage.removeItem("sejour_login_at"); setUser(null); setScreen("login"); }}
          onGoSettings={() => { setPrevScreen("dashboard"); setScreen("settings"); }}
          showToast={showToast} openNewTrip={openNewTrip} />
      )}
      {screen === "home-dashboard" && user && (
        <HomeDashboard
          user={user} updateUser={updateUser}
          monthlyExpenses={monthlyExpenses} setMonthlyExpenses={setMonthlyExpenses}
          allMonthlyData={allMonthlyData} setAllMonthlyData={setAllMonthlyData}
          monthlyBudgets={monthlyBudgets} setMonthlyBudgets={setMonthlyBudgets}
          homeView={homeView} setHomeView={setHomeView}
          currentMonth={currentMonth}
          onChangeMonth={async (yr, mo) => {
            setCurrentMonth({ year: yr, month: mo });
            const exps = await loadMonthlyExpenses(user.id, yr, mo);
            setMonthlyExpenses(exps);
          }}
          onLoadAllData={async () => {
            const data = await loadAllMonthlyData(user.id);
            setAllMonthlyData(data);
            const budgets = await loadAllMonthlyBudgets(user.id);
            setMonthlyBudgets(budgets);
          }}
          mode={mode} switchMode={(m) => {
            switchMode(m);
            if (m === "travel") setScreen("dashboard");
          }}
          onLogout={() => { localStorage.removeItem("sejour_user_id"); localStorage.removeItem("sejour_login_at"); setUser(null); setScreen("login"); }}
          onGoSettings={() => { setPrevScreen("home-dashboard"); setScreen("settings"); }}
          showToast={showToast}
        />
      )}
      {screen === "trip" && currentTrip && (
        <TripDetail trip={currentTrip} user={user} updateTrips={updateTrips}
          showToast={showToast} firstTrip={firstTrip}
          onBack={() => { setFirstTrip(false); setOpenNewTrip(false); setScreen("dashboard"); }}
          onSettings={() => { setPrevScreen("trip"); setScreen("settings"); }}
          onDeleteTrip={async () => {
            await supabase.from("trips").delete().eq("id", tripId).eq("user_id", user.id);
            updateTrips(ts => ts.filter(t => t.id !== tripId));
            setFirstTrip(false); setScreen("dashboard"); showToast("Trip deleted.");
          }} />
      )}
      {screen === "settings" && user && (
        <SettingsPage user={user} updateUser={updateUser}
          onBack={() => setScreen(prevScreen)}
          onLogout={() => { localStorage.removeItem("sejour_user_id"); localStorage.removeItem("sejour_login_at"); setUser(null); setScreen("login"); }}
          showToast={showToast} />
      )}
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [tab,          setTab]          = useState("signin");
  const [code,         setCode]         = useState("");
  const [name,         setName]         = useState("");
  const [email,        setEmail]        = useState("");
  const [homeCurrency, setHomeCurrency] = useState("SGD");
  const [error,        setError]        = useState("");
  const [step,         setStep]         = useState(null);
  const [created,      setCreated]      = useState(null);
  const [loading,      setLoading]      = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [lockoutUntil,   setLockoutUntil]   = useState(null);
  const [forgotEmail,    setForgotEmail]    = useState("");
  const [forgotSent,     setForgotSent]     = useState(false);
  const [forgotLoading,  setForgotLoading]  = useState(false);
  const [forgotError,    setForgotError]    = useState("");

  const MAX_ATTEMPTS  = 5;
  const LOCKOUT_MS    = 15 * 60 * 1000; // 15 minutes

  async function handleSignIn() {
    // Check lockout
    if (lockoutUntil && Date.now() < lockoutUntil) {
      const mins = Math.ceil((lockoutUntil - Date.now()) / 60000);
      setError(`Too many failed attempts. Try again in ${mins} minute${mins !== 1 ? "s" : ""}.`);
      return;
    }
    setError(""); setLoading(true);
    const { data, error } = await supabase.from("users").select("*").eq("access_code", code.trim().toUpperCase()).single();
    setLoading(false);
    if (error || !data) {
      const next = failedAttempts + 1;
      setFailedAttempts(next);
      if (next >= MAX_ATTEMPTS) {
        setLockoutUntil(Date.now() + LOCKOUT_MS);
        setError("Too many failed attempts. You're locked out for 15 minutes.");
      } else {
        setError(`Code not found. ${MAX_ATTEMPTS - next} attempt${MAX_ATTEMPTS - next !== 1 ? "s" : ""} remaining.`);
      }
      return;
    }
    // Reset on success
    setFailedAttempts(0); setLockoutUntil(null);
    const { data: trips } = await supabase.from("trips").select("*").eq("user_id", data.id).order("created_at", { ascending: false });
    const tripList = await Promise.all((trips || []).map(async t => {
      const { data: expenses } = await supabase.from("expenses").select("*").eq("trip_id", t.id);
      return {
        id: t.id, name: t.name, destination: t.destination,
        startDate: t.start_date, endDate: t.end_date,
        currency: t.currency, budget: t.budget || "", colour: t.colour || "teal", synced: t.synced || false,
        expenses: (expenses || []).map(e => ({ id: e.id, category: e.category, description: e.description, amount: e.amount, currency: e.currency, date: e.date, notes: e.notes || "" })),
      };
    }));
    const appUser = { id: data.id, accessCode: data.access_code, displayName: data.display_name, homeCurrency: data.home_currency, hasGoogle: data.has_google || false, spreadsheetId: data.spreadsheet_id || null, email: data.email || "", trips: tripList };
    localStorage.setItem("sejour_user_id", data.id);
    localStorage.setItem("sejour_login_at", Date.now().toString());
    onLogin(appUser);
  }

  async function handleForgot() {
    if (!forgotEmail.trim()) { setForgotError("Please enter your email."); return; }
    setForgotLoading(true); setForgotError("");
    try {
      await fetch(EDGE_FN_URL + "/send-code", { method: "POST", headers: EDGE_HEADERS, body: JSON.stringify({ email: forgotEmail.trim() }) });
      setForgotSent(true);
    } catch {
      setForgotError("Something went wrong. Please try again.");
    }
    setForgotLoading(false);
  }

  async function handleCreate() {
    setError(""); setLoading(true);
    if (name.trim().length < 2) { setError("Name must be at least 2 characters."); setLoading(false); return; }
    const accessCode = genCode(name);
    const insertData: any = { display_name: name.trim(), access_code: accessCode, home_currency: homeCurrency, has_google: false };
    if (email.trim()) insertData.email = email.trim().toLowerCase();
    const { data, error } = await supabase.from("users").insert(insertData).select().single();
    setLoading(false);
    if (error) { setError("DB Error: " + error.message); return; }
    if (!data) { setError("No data returned — please try again."); return; }
    const newUser = { id: data.id, accessCode: data.access_code, displayName: data.display_name, homeCurrency: data.home_currency, hasGoogle: false, spreadsheetId: null, email: data.email || "", trips: [] };
    localStorage.setItem("sejour_user_id", data.id);
    localStorage.setItem("sejour_login_at", Date.now().toString());
    setCreated(newUser); setStep("code");
  }

  if (step === "code" && created) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"16px" }}>
        <div style={{ ...S.card, width:"100%", maxWidth:"420px", padding:"clamp(20px, 5vw, 32px)", textAlign:"center" }}>
          <div style={{ width:48, height:48, background:"#f0fdf4", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
            <CheckCircle size={22} color="#16a34a" />
          </div>
          <h2 style={{ fontSize:"22px", fontWeight:700, marginBottom:"6px" }}>Welcome, {created.displayName}.</h2>
          <p style={{ color:"#7a7060", fontSize:"14px", marginBottom:"24px", lineHeight:1.6 }}>
            Your account is ready. Save this access code — it's the only way to sign in from any device.
          </p>
          <div style={{ fontFamily:"monospace", fontSize:"24px", letterSpacing:"0.3em", background:"#f8f7f4", padding:"16px", borderRadius:"12px", marginBottom:"8px", border:"2px solid #e5e0d8", fontWeight:600 }}>
            {created.accessCode}
          </div>
          <p style={{ color:"#a39888", fontSize:"11px", marginBottom:"20px", display:"flex", alignItems:"center", justifyContent:"center", gap:"4px" }}>
            <AlertCircle size={11} /> {created.email ? "Recoverable via your email" : "Write this down — it cannot be recovered without an email"}
          </p>
          <a href={"mailto:?subject=My SÉJOUR Access Code&body=Hi, here is your SÉJOUR access code:%0A%0A" + created.accessCode + "%0A%0AKeep this safe — it is the only way to sign in to your account.%0A%0A— SÉJOUR"}
            style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:"6px", width:"100%", padding:"10px", background:"#f4f2ef", color:"#625a4d", border:"1.5px solid #e5e0d8", borderRadius:"10px", fontSize:"13px", fontWeight:500, marginBottom:"10px", textDecoration:"none", boxSizing:"border-box" }}>
            <FileText size={14} /> Email it to yourself (optional)
          </a>
          <button style={{ ...S.btnPrimary, width:"100%", padding:"13px", fontSize:"14px" }} onClick={() => setStep("welcome")}>
            I've saved it — continue
          </button>
        </div>
      </div>
    );
  }

  if (step === "welcome" && created) {
    return (
      <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"16px" }}>
        <div style={{ ...S.card, width:"100%", maxWidth:"420px", padding:"clamp(20px, 5vw, 32px)" }}>
          <div style={{ marginBottom:"24px" }}>
            <p style={{ fontSize:"13px", color:"#a39888", marginBottom:"6px" }}>You're all set</p>
            <h2 style={{ fontSize:"26px", fontWeight:700, marginBottom:"8px", lineHeight:1.2 }}>Ready to track your first trip?</h2>
            <p style={{ color:"#7a7060", fontSize:"14px", lineHeight:1.6 }}>Add a trip now and start logging expenses as you go — or head to your dashboard and set one up later.</p>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
            <button style={{ ...S.btnPrimary, width:"100%", padding:"13px", fontSize:"14px" }} onClick={() => { localStorage.setItem("sejour_login_at", Date.now().toString()); onLogin(created, false, true); }}>
              <Plus size={15} /> Add my first trip
            </button>
            <button style={{ ...S.btnSecondary, width:"100%", padding:"12px", fontSize:"13px" }} onClick={() => { localStorage.setItem("sejour_login_at", Date.now().toString()); onLogin(created, false, true); }}>
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"16px" }}>
      <div style={{ textAlign:"center", marginBottom:"28px" }}>
        <div style={{ width:52, height:52, background:"#1a1714", borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <Plane size={26} color="#f5f0e8" />
        </div>
        <h1 style={{ fontSize:"36px", fontWeight:700, marginBottom:"6px", letterSpacing:"0.22em" }}>SÉJOUR</h1>
        <p style={{ color:"#7a7060", fontSize:"13px" }}>Every journey, accounted for.</p>
      </div>
      <div style={{ ...S.card, width:"100%", maxWidth:"420px", padding:"clamp(16px, 4vw, 24px)" }}>
        <div style={{ display:"flex", gap:"3px", background:"#f4f2ef", padding:"3px", borderRadius:"10px", marginBottom:"20px" }}>
          {["signin","create"].map(t => (
            <button key={t} onClick={() => { setTab(t); setError(""); }}
              style={{ flex:1, padding:"8px", borderRadius:"7px", border:"none", fontSize:"13px", fontWeight:500, cursor:"pointer", fontFamily:"inherit", background: tab===t ? "white" : "transparent", color: tab===t ? "#1a1714" : "#7a7060", boxShadow: tab===t ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
              {t === "signin" ? "Sign In" : "New Account"}
            </button>
          ))}
        </div>
        {tab === "signin" ? (
          step === "forgot" ? (
            <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
              {forgotSent ? (
                <div style={{ textAlign:"center", padding:"8px 0" }}>
                  <div style={{ width:44, height:44, background:"#f0fdf4", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}><CheckCircle size={20} color="#16a34a" /></div>
                  <p style={{ fontSize:"14px", color:"#1a1714", fontWeight:600, marginBottom:"6px" }}>Check your inbox</p>
                  <p style={{ fontSize:"13px", color:"#7a7060", lineHeight:1.6 }}>If that email is on file, we've sent your access code.</p>
                  <button style={{ ...S.btnGhost, marginTop:"16px", width:"100%" }} onClick={() => { setStep(null); setForgotSent(false); setForgotEmail(""); }}>Back to sign in</button>
                </div>
              ) : (
                <>
                  <div>
                    <label style={S.label}>Recovery Email</label>
                    <input style={S.input} type="email" placeholder="you@example.com" value={forgotEmail}
                      onChange={e => { setForgotEmail(e.target.value); setForgotError(""); }}
                      onKeyDown={e => e.key === "Enter" && handleForgot()} autoFocus />
                  </div>
                  {forgotError && <p style={{ color:"#dc2626", fontSize:"13px", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={13} />{forgotError}</p>}
                  <button style={{ ...S.btnPrimary, width:"100%" }} onClick={handleForgot} disabled={forgotLoading}>
                    {forgotLoading ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : <Send size={14} />}
                    {forgotLoading ? "Sending…" : "Send my code"}
                  </button>
                  <button style={{ ...S.btnGhost, width:"100%" }} onClick={() => { setStep(null); setForgotError(""); setForgotEmail(""); }}>Back to sign in</button>
                </>
              )}
            </div>
          ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
            <div>
              <label style={S.label}>Access Code</label>
              <input style={{ ...S.input, fontFamily:"monospace", textAlign:"center", fontSize:"20px", letterSpacing:"0.25em", textTransform:"uppercase" }}
                value={code} onChange={e => { setCode(e.target.value.toUpperCase()); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleSignIn()}
                placeholder="XXXXXXXX" maxLength={8} autoFocus spellCheck={false} autoComplete="off" />
            </div>
            {error && <p style={{ color:"#dc2626", fontSize:"13px", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={13} />{error}</p>}
            <button style={{ ...S.btnPrimary, width:"100%" }} onClick={handleSignIn} disabled={loading}>
              {loading ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : <Lock size={14} />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
            <button style={{ background:"none", border:"none", color:"#a39888", fontSize:"12px", cursor:"pointer", fontFamily:"inherit", textDecoration:"underline", textDecorationColor:"#d1c9bf", padding:0, textAlign:"center" }}
              onClick={() => { setStep("forgot"); setError(""); }}>
              Forgot your code?
            </button>
          </div>
          )
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"14px" }}>
            <div>
              <label style={S.label}>Your Name</label>
              <input style={S.input} value={name} onChange={e => { setName(e.target.value); setError(""); }} onKeyDown={e => e.key === "Enter" && handleCreate()} autoFocus />
            </div>
            <div>
              <label style={S.label}>Home Currency</label>
              <select style={S.input} value={homeCurrency} onChange={e => setHomeCurrency(e.target.value)}>
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <p style={{ fontSize:"12px", color:"#a39888", lineHeight:1.5 }}>No email needed. A unique access code will be your login.</p>
            <div>
              <label style={S.label}>Email <span style={{ color:"#a39888", fontWeight:400 }}>(optional — for code recovery)</span></label>
              <input style={S.input} type="email" placeholder="you@example.com" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} />
            </div>
            {error && <p style={{ color:"#dc2626", fontSize:"13px" }}>{error}</p>}
            <button style={{ ...S.btnPrimary, width:"100%" }} onClick={handleCreate} disabled={loading}>
              {loading ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : null}
              {loading ? "Creating…" : "Create account"}
            </button>
          </div>
        )}
      </div>
      <p style={{ marginTop:"20px", fontSize:"11px", color:"#a39888", textAlign:"center" }}>
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color:"#a39888", textDecoration:"none", borderBottom:"1px solid #d1c9bf" }}>Privacy Policy</a>
        <span style={{ margin:"0 8px" }}>·</span>
        <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color:"#a39888", textDecoration:"none", borderBottom:"1px solid #d1c9bf" }}>Terms of Service</a>
      </p>
    </div>
  );
}

function Dashboard({ user, updateUser, updateTrips, onOpenTrip, onLogout, onGoSettings, showToast, openNewTrip, mode, switchMode }) {
  const [showNew,            setShowNew]            = useState(openNewTrip || false);
  const [statusFilter,       setStatusFilter]       = useState("all");
  const [showCurrencyPicker, setShowCurrencyPicker] = useState(false);
  const { loading: ratesLoading, error: ratesError, lastUpdated, refresh, convert } = useRates(user.homeCurrency);

  async function handleCreate(trip) {
    const isFirst = user.trips.length === 0;
    const { data, error } = await supabase.from("trips").insert({
      user_id: user.id, name: trip.name, destination: trip.destination,
      start_date: trip.startDate, end_date: trip.endDate,
      currency: trip.currency, budget: trip.budget || null,
      colour: trip.colour || "teal", synced: false,
    }).select().single();
    if (error) { showToast("Could not save trip. Try again.", "error"); return; }
    const savedTrip = { ...trip, id: data.id };
    updateTrips(ts => [savedTrip, ...ts]);
    setShowNew(false);
    if (isFirst) onOpenTrip(savedTrip.id, true);
    else showToast(trip.name + " created!");
  }

  async function handleSetHomeCurrency(c) {
    updateUser(u => ({ ...u, homeCurrency: c }));
    await supabase.from("users").update({ home_currency: c }).eq("id", user.id);
    showToast("Home currency updated to " + c);
  }

  return (
    <div>
      <div style={S.topbar}>
        <div style={{ width:30, height:30, background:"#1a1714", borderRadius:"8px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Plane size={16} color="#f5f0e8" />
        </div>
        <span style={{ fontWeight:700, fontSize:"15px", letterSpacing:"0.2em" }}>SÉJOUR</span>
        <div style={{ display:"flex", gap:"2px", background:"#f4f2ef", padding:"3px", borderRadius:"8px", marginLeft:"4px" }}>
          {["travel","home"].map(id => (
            <button key={id} onClick={() => switchMode(id)} style={{ padding:"5px 12px", borderRadius:"6px", border:"none", fontSize:"12px", fontWeight:500, cursor:"pointer", fontFamily:"inherit", background: mode===id ? "white" : "transparent", color: mode===id ? "#1a1714" : "#7a7060", boxShadow: mode===id ? "0 1px 3px rgba(0,0,0,0.1)" : "none", transition:"all .15s" }}>
              {id.charAt(0).toUpperCase() + id.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ flex:1 }} />
        {ratesLoading && <div style={{ display:"flex", alignItems:"center", gap:"5px", fontSize:"12px", color:"#958b79" }}><Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} />Fetching rates…</div>}
        {lastUpdated && !ratesLoading && (
          <button onClick={refresh} className="desktop-only" style={{ ...S.btnGhost, fontSize:"11px", color:"#a39888", padding:"4px 8px" }}>
            <RefreshCw size={11} /> Rates updated
          </button>
        )}
        <div style={{ position:"relative" }}>
          <button onClick={() => setShowCurrencyPicker(p => !p)} style={{ fontFamily:"monospace", fontWeight:700, fontSize:"12px", background:"#f4f2ef", border:"1.5px solid #e5e0d8", borderRadius:"7px", padding:"5px 9px", cursor:"pointer", color:"#1a1714", letterSpacing:"0.05em" }}>
            {user.homeCurrency}
          </button>
          {showCurrencyPicker && (
            <>
            <div style={{ position:"fixed", inset:0, zIndex:49 }} onClick={() => setShowCurrencyPicker(false)} />
            <div style={{ position:"absolute", right:0, top:"calc(100% + 6px)", background:"white", border:"1px solid #e5e0d8", borderRadius:"10px", boxShadow:"0 4px 16px rgba(0,0,0,0.12)", zIndex:50, width:"160px", maxHeight:"240px", overflowY:"auto", padding:"6px" }}>
              {CURRENCIES.map(c => (
                <button key={c} onClick={() => { handleSetHomeCurrency(c); setShowCurrencyPicker(false); }}
                  style={{ display:"block", width:"100%", textAlign:"left", padding:"7px 10px", border:"none", background: c === user.homeCurrency ? "#f4f2ef" : "transparent", fontFamily:"monospace", fontSize:"13px", fontWeight: c === user.homeCurrency ? 700 : 400, cursor:"pointer", borderRadius:"6px", color:"#1a1714" }}>
                  {c}
                </button>
              ))}
            </div>
            </>
          )}
        </div>
        <button style={S.btnGhost} onClick={onGoSettings} title="Settings"><Settings size={15} /></button>
        <button style={{ ...S.btnGhost, color:"#958b79" }} onClick={onLogout}><LogOut size={14} /></button>
      </div>
      {ratesError && (
        <div style={{ background:"#fffbeb", borderBottom:"1px solid #fde68a", padding:"8px 20px", fontSize:"12px", color:"#92400e", display:"flex", alignItems:"center", gap:"6px" }}>
          <AlertCircle size={13} /> {ratesError}
        </div>
      )}
      <div style={{ maxWidth:860, margin:"0 auto", padding:"16px clamp(12px, 3vw, 20px)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px" }}>
          <div>
            <h1 style={{ fontSize:"clamp(22px, 6vw, 30px)", fontWeight:700, marginBottom:"4px" }}>Hello, {user.displayName}.</h1>
            <p style={{ color:"#7a7060", fontSize:"14px", marginBottom:"2px" }}>Where to next?</p>
            <p style={{ color:"#a39888", fontSize:"12px" }}>{user.trips.length} trip{user.trips.length !== 1 ? "s" : ""}</p>
          </div>
          <button style={{ ...S.btnPrimary, width:"auto" }} onClick={() => setShowNew(true)}><Plus size={15} /> New Trip</button>
        </div>
        {user.trips.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px 0" }}>
            <div style={{ width:60, height:60, background:"#f0f0ee", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 16px" }}>
              <MapPin size={28} color="#a39888" />
            </div>
            <h2 style={{ fontSize:"20px", fontWeight:600, marginBottom:"6px" }}>No trips yet</h2>
            <p style={{ color:"#7a7060", fontSize:"14px" }}>Use the button above to add your first trip.</p>
          </div>
        ) : (() => {
          const STATUS_ORDER = { upcoming: 0, active: 1, past: 2 };
          const sorted = [...user.trips].sort((a, b) => {
            const sa = getTripStatus(a), sb = getTripStatus(b);
            if (STATUS_ORDER[sa] !== STATUS_ORDER[sb]) return STATUS_ORDER[sa] - STATUS_ORDER[sb];
            return new Date(a.startDate) - new Date(b.startDate);
          });
          const filtered = statusFilter === "all" ? sorted : sorted.filter(t => getTripStatus(t) === statusFilter);
          return (
            <div>
              <div style={{ display:"flex", gap:"6px", marginBottom:"16px", flexWrap:"wrap" }}>
                {["all","upcoming","active","past"].map(f => (
                  <button key={f} onClick={() => setStatusFilter(f)}
                    style={{ padding:"5px 14px", borderRadius:"999px", border:"1.5px solid", fontSize:"12px", fontWeight:500, cursor:"pointer", fontFamily:"inherit", transition:"all .15s",
                      borderColor: statusFilter===f ? "#1a1714" : "#e5e0d8", background: statusFilter===f ? "#1a1714" : "white", color: statusFilter===f ? "#f5f0e8" : "#625a4d" }}>
                    {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
                    {f !== "all" && <span style={{ marginLeft:"5px", opacity:0.7 }}>{sorted.filter(t => getTripStatus(t) === f).length}</span>}
                  </button>
                ))}
              </div>
              {filtered.length === 0 ? (
                <div style={{ textAlign:"center", padding:"48px 0", color:"#a39888", fontSize:"14px" }}>No {statusFilter} trips.</div>
              ) : (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(250px, 1fr))", gap:"14px" }}>
                  {filtered.map(trip => (
                    <TripCard key={trip.id} trip={trip} homeCurrency={user.homeCurrency} convert={convert} ratesLoading={ratesLoading} onClick={() => onOpenTrip(trip.id)} />
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
      {showNew && <NewTripModal onClose={() => setShowNew(false)} onCreate={handleCreate} defaultCurrency={user.homeCurrency} homeCurrency={user.homeCurrency} convert={convert} ratesLoading={ratesLoading} />}
    </div>
  );
}

function ContactCard({ userId, userName, showToast }) {
  const [email,    setEmail]    = useState("");
  const [message,  setMessage]  = useState("");
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);

  async function handleSend() {
    if (!message.trim() || !email.trim()) return;
    setSending(true);
    try {
      const res = await fetch(EDGE_FN_URL + "/contact", {
        method: "POST",
        headers: EDGE_HEADERS,
        body: JSON.stringify({ userId, userName, userEmail: email.trim(), message: message.trim() }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Failed");
      setSent(true);
      setMessage("");
      setEmail("");
      setTimeout(() => setSent(false), 5000);
    } catch(e) {
      showToast("Could not send message. Please try again.", "error");
    }
    setSending(false);
  }

  return (
    <div style={{ ...S.card, padding:"24px" }}>
      <h2 style={{ fontSize:"14px", fontWeight:700, marginBottom:"6px", display:"flex", alignItems:"center", gap:"6px" }}><MessageCircle size={14} /> Leave a message</h2>
      <p style={{ fontSize:"13px", color:"#7a7060", marginBottom:"16px", lineHeight:1.5 }}>Have a question, found a bug, or want to suggest a feature? Leave your email so we can get back to you.</p>
      {sent ? (
        <div style={{ display:"flex", alignItems:"center", gap:"8px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:"10px", padding:"12px 14px", fontSize:"13px", color:"#166534" }}>
          <CheckCircle size={15} /> Message sent — we'll be in touch!
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
          <div>
            <label style={S.label}>Your email</label>
            <input style={S.input} type="email" placeholder="you@example.com" value={email} onChange={e => setEmail(e.target.value)} />
          </div>
          <div>
            <label style={S.label}>Message</label>
            <textarea style={{ ...S.input, minHeight:"100px", resize:"vertical", fontFamily:"inherit", lineHeight:1.6 }}
              placeholder="Type your message here…" value={message} onChange={e => setMessage(e.target.value)} />
          </div>
          <button style={{ ...S.btnPrimary, width:"auto", alignSelf:"flex-end", display:"flex", alignItems:"center", gap:"6px" }}
            onClick={handleSend} disabled={sending || !message.trim() || !email.trim()}>
            {sending ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : <Send size={14} />}
            {sending ? "Sending…" : "Send message"}
          </button>
        </div>
      )}
    </div>
  );
}

function SettingsPage({ user, updateUser, onBack, onLogout, showToast }) {
  const [editingName,    setEditingName]    = useState(false);
  const [nameVal,        setNameVal]        = useState(user.displayName);
  const [codeCopied,     setCodeCopied]     = useState(false);
  const [editingCode,    setEditingCode]    = useState(false);
  const [newCode,        setNewCode]        = useState("");
  const [codeError,      setCodeError]      = useState("");
  const [codeSaving,     setCodeSaving]     = useState(false);
  const [editingEmail,   setEditingEmail]   = useState(false);
  const [emailVal,       setEmailVal]       = useState(user.email || "");
  const [emailError,     setEmailError]     = useState("");
  const [emailSaving,    setEmailSaving]    = useState(false);
  const [editingBudget,  setEditingBudget]  = useState(false);
  const [budgetVal,      setBudgetVal]      = useState(user.monthlyBudget ? String(user.monthlyBudget) : "");
  const [budgetSaving,   setBudgetSaving]   = useState(false);
  const [confirmDelete,     setConfirmDelete]     = useState(false);
  const [disconnecting,     setDisconnecting]     = useState(false);
  const [deleteLoading,  setDeleteLoading]  = useState(false);

  async function handleDeleteAccount() {
    setDeleteLoading(true);
    try {
      await supabase.from("expenses").delete().in("trip_id",
        (await supabase.from("trips").select("id").eq("user_id", user.id)).data?.map(t => t.id) || []
      );
      await supabase.from("trips").delete().eq("user_id", user.id);
      await supabase.from("users").delete().eq("id", user.id);
      localStorage.removeItem("sejour_user_id");
      window.location.reload();
    } catch(e) {
      showToast("Could not delete account. Please try again.", "error");
      setDeleteLoading(false);
    }
  }

  async function saveEmail() {
    const val = emailVal.trim().toLowerCase();
    if (val === (user.email || "")) { setEditingEmail(false); return; }
    if (val && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) { setEmailError("Invalid email address."); return; }
    setEmailSaving(true); setEmailError("");
    const { error } = await supabase.from("users").update({ email: val || null }).eq("id", user.id);
    if (error) { setEmailError("Could not update email. Please try again."); setEmailSaving(false); return; }
    updateUser(u => ({ ...u, email: val }));
    showToast(val ? "Email saved!" : "Email removed.");
    setEditingEmail(false); setEmailSaving(false);
  }

  async function saveBudget() {
    const val = budgetVal.trim();
    const num = val ? parseFloat(val) : null;
    setBudgetSaving(true);
    await supabase.from("users").update({ monthly_budget: num }).eq("id", user.id);
    updateUser(u => ({ ...u, monthlyBudget: num || "" }));
    showToast(num ? "Monthly budget saved!" : "Monthly budget removed.");
    setEditingBudget(false); setBudgetSaving(false);
  }

  async function saveName() {
    if (!nameVal.trim() || nameVal.trim() === user.displayName) { setEditingName(false); return; }
    await supabase.from("users").update({ display_name: nameVal.trim() }).eq("id", user.id);
    updateUser(u => ({ ...u, displayName: nameVal.trim() }));
    showToast("Name updated!"); setEditingName(false);
  }

  function copyCode() {
    navigator.clipboard.writeText(user.accessCode).then(() => { setCodeCopied(true); setTimeout(() => setCodeCopied(false), 2000); });
  }

  async function saveCode() {
    const val = newCode.trim().toUpperCase();
    if (!val) return;
    if (val.length < 8) { setCodeError("Code must be at least 8 characters."); return; }
    if (val === user.accessCode) { setEditingCode(false); setNewCode(""); return; }
    setCodeSaving(true); setCodeError("");
    const { data: existing } = await supabase.from("users").select("id").eq("access_code", val).single();
    if (existing) { setCodeError("That code is already taken. Try another."); setCodeSaving(false); return; }
    const { error } = await supabase.from("users").update({ access_code: val }).eq("id", user.id);
    if (error) { setCodeError("Could not update code. Please try again."); setCodeSaving(false); return; }
    updateUser(u => ({ ...u, accessCode: val }));
    localStorage.setItem("sejour_user_id", user.id);
    showToast("Access code updated!");
    setEditingCode(false); setNewCode(""); setCodeSaving(false);
  }

  return (
    <div>
      <div style={S.topbar}>
        <button style={{ ...S.btnGhost, padding:"5px 8px" }} onClick={onBack}><ArrowLeft size={18} /></button>
        <div style={{ width:30, height:30, background:"#1a1714", borderRadius:"8px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Plane size={16} color="#f5f0e8" />
        </div>
        <span style={{ fontWeight:700, fontSize:"15px", flex:1, letterSpacing:"0.2em" }}>Settings</span>
        <button style={{ ...S.btnGhost, color:"#958b79" }} onClick={onLogout}><LogOut size={14} /> Sign out</button>
      </div>
      <div style={{ maxWidth:560, margin:"0 auto", padding:"clamp(16px, 4vw, 36px) clamp(12px, 4vw, 20px)", display:"flex", flexDirection:"column", gap:"16px" }}>
        <div style={{ ...S.card, padding:"24px" }}>
          <h2 style={{ fontSize:"14px", fontWeight:700, marginBottom:"16px", display:"flex", alignItems:"center", gap:"6px", color:"#1a1714" }}><User size={14} /> Profile</h2>
          <div style={{ marginBottom:"16px" }}>
            <label style={S.label}>Display Name</label>
            {editingName ? (
              <div style={{ display:"flex", gap:"8px" }}>
                <input style={{ ...S.input }} value={nameVal} onChange={e => setNameVal(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setEditingName(false); setNameVal(user.displayName); } }} autoFocus />
                <button style={{ ...S.btnPrimary, width:"auto", padding:"8px 12px" }} onClick={saveName}><CheckCircle size={14} /></button>
                <button style={S.btnGhost} onClick={() => { setEditingName(false); setNameVal(user.displayName); }}><X size={14} /></button>
              </div>
            ) : (
              <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
                <div style={{ ...S.input, cursor:"text", flex:1 }} onClick={() => setEditingName(true)}>{user.displayName}</div>
              </div>
            )}
            <p style={{ fontSize:"11px", color:"#a39888", marginTop:"5px" }}>Click your name to rename</p>
          </div>
        </div>
        <div style={{ ...S.card, padding:"24px" }}>
          <h2 style={{ fontSize:"14px", fontWeight:700, marginBottom:"6px", display:"flex", alignItems:"center", gap:"6px", color:"#1a1714" }}><Send size={14} /> Recovery Email</h2>
          <p style={{ fontSize:"13px", color:"#7a7060", marginBottom:"16px", lineHeight:1.5 }}>Used to recover your access code if you forget it. Never used for anything else.</p>
          {editingEmail ? (
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              <div style={{ display:"flex", gap:"8px" }}>
                <input style={{ ...S.input, flex:1 }} type="email" placeholder="you@example.com" value={emailVal}
                  onChange={e => { setEmailVal(e.target.value); setEmailError(""); }}
                  onKeyDown={e => { if (e.key === "Enter") saveEmail(); if (e.key === "Escape") { setEditingEmail(false); setEmailVal(user.email || ""); setEmailError(""); } }}
                  autoFocus />
                <button style={{ ...S.btnPrimary, width:"auto", padding:"8px 12px" }} onClick={saveEmail} disabled={emailSaving}>
                  {emailSaving ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : <CheckCircle size={14} />}
                </button>
                <button style={S.btnGhost} onClick={() => { setEditingEmail(false); setEmailVal(user.email || ""); setEmailError(""); }}><X size={14} /></button>
              </div>
              {emailError && <p style={{ fontSize:"12px", color:"#dc2626", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={11} /> {emailError}</p>}
            </div>
          ) : (
            <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
              <div style={{ ...S.input, flex:1, cursor:"text", color: user.email ? "#1a1714" : "#a39888" }} onClick={() => setEditingEmail(true)}>
                {user.email || "No email set"}
              </div>
              <button style={{ ...S.btnSecondary, flexShrink:0 }} onClick={() => setEditingEmail(true)}><FileText size={14} /> {user.email ? "Edit" : "Add"}</button>
            </div>
          )}
          <p style={{ fontSize:"11px", color:"#a39888", marginTop:"6px" }}>Click to {user.email ? "update" : "add"} your recovery email</p>
        </div>

        <div style={{ ...S.card, padding:"24px" }}>
          <h2 style={{ fontSize:"14px", fontWeight:700, marginBottom:"6px", display:"flex", alignItems:"center", gap:"6px" }}><Lock size={14} /> Access Code</h2>
          <p style={{ fontSize:"13px", color:"#7a7060", marginBottom:"16px", lineHeight:1.5 }}>This is your only way to sign in. Keep it somewhere safe.</p>
          {editingCode ? (
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              <div style={{ display:"flex", gap:"8px" }}>
                <input
                  style={{ ...S.input, fontFamily:"monospace", letterSpacing:"0.15em", textTransform:"uppercase", flex:1 }}
                  value={newCode}
                  onChange={e => { setNewCode(e.target.value.toUpperCase()); setCodeError(""); }}
                  onKeyDown={e => { if (e.key === "Enter") saveCode(); if (e.key === "Escape") { setEditingCode(false); setNewCode(""); setCodeError(""); } }}
                  placeholder="NEW CODE" autoFocus maxLength={20} />
                <button style={{ ...S.btnPrimary, width:"auto", padding:"8px 12px" }} onClick={saveCode} disabled={codeSaving || !newCode.trim()}>
                  {codeSaving ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : <CheckCircle size={14} />}
                </button>
                <button style={S.btnGhost} onClick={() => { setEditingCode(false); setNewCode(""); setCodeError(""); }}><X size={14} /></button>
              </div>
              {codeError && <p style={{ fontSize:"12px", color:"#dc2626", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={11} /> {codeError}</p>}
            </div>
          ) : (
            <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
              <div style={{ fontFamily:"monospace", fontSize:"20px", letterSpacing:"0.25em", background:"#f8f7f4", padding:"10px 14px", borderRadius:"10px", border:"1px solid #e5e0d8", fontWeight:700, flex:1, cursor:"text" }}
                onClick={() => { setEditingCode(true); setNewCode(user.accessCode); }}>
                {user.accessCode}
              </div>
              <button style={{ ...S.btnSecondary, flexShrink:0 }} onClick={copyCode}>
                {codeCopied ? <><CheckCircle size={14} color="#16a34a" /> Copied</> : <><FileText size={14} /> Copy</>}
              </button>
            </div>
          )}
          <p style={{ fontSize:"11px", color:"#a39888", marginTop:"6px" }}>Click your code to change it</p>
          <p style={{ fontSize:"11px", color:"#a39888", marginTop:"4px", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={11} /> {user.email ? "Recoverable via your email if lost" : "Add a recovery email to recover this if lost"}</p>
        </div>
        <div style={{ ...S.card, padding:"24px" }}>
          <h2 style={{ fontSize:"14px", fontWeight:700, marginBottom:"6px", display:"flex", alignItems:"center", gap:"6px" }}><BarChart3 size={14} /> Google Sheets</h2>
          <p style={{ fontSize:"13px", color:"#7a7060", marginBottom:"12px", lineHeight:1.5 }}>All trips sync to one shared sheet — <strong>SÉJOUR — {user.displayName}</strong> — each trip as its own tab.</p>
          {user.hasGoogle ? (
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"8px", background:"#f0fdf4", border:"1px solid #bbf7d0", borderRadius:"10px", padding:"12px 14px", fontSize:"13px", color:"#166534" }}>
                <CheckCircle size={15} /> Google account connected
              </div>
              <button style={{ ...S.btnSecondary, width:"auto", fontSize:"12px" }} disabled={disconnecting} onClick={async () => {
                setDisconnecting(true);
                try {
                  await fetch(EDGE_FN_URL + "/disconnect", { method:"POST", headers:EDGE_HEADERS, body:JSON.stringify({ userId: user.id }) });
                } catch(e) {}
                await supabase.from("users").update({ has_google: false, google_access_token: null, google_refresh_token: null, google_token_expiry: null, spreadsheet_id: null }).eq("id", user.id);
                updateUser(u => ({ ...u, hasGoogle: false, spreadsheetId: null }));
                setDisconnecting(false);
                showToast("Google Sheets disconnected.");
              }}>
                {disconnecting ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <X size={13} />}
                {disconnecting ? "Disconnecting…" : "Disconnect Google Sheets"}
              </button>
            </div>
          ) : (
            <button style={{ ...S.btnSecondary, width:"auto" }} onClick={async () => {
              try {
                const res = await fetch(EDGE_FN_URL + "/auth-url", { method:"POST", headers:EDGE_HEADERS, body:JSON.stringify({ userId: user.id }) });
                const data = await res.json();
                if (data.url) window.location.href = data.url;
                else showToast("Could not start Google login.", "error");
              } catch(e) { showToast("Could not start Google login.", "error"); }
            }}>
              <BarChart3 size={14} /> Connect Google Sheets
            </button>
          )}
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"10px", padding:"4px 0" }}>
          <p style={{ textAlign:"center", fontSize:"12px", color:"#a39888", lineHeight:1.6, margin:0 }}>SÉJOUR is a free, indie app built by one person. If it's been useful, a small tip goes a long way.</p>
          <a href="https://ko-fi.com/helloitsaverie" target="_blank" rel="noopener noreferrer"
            style={{ display:"inline-flex", alignItems:"center", gap:"6px", padding:"8px 18px", background:"#1a1714", color:"#f5f0e8", borderRadius:"999px", fontSize:"12px", fontWeight:600, textDecoration:"none", fontFamily:"inherit", letterSpacing:"0.02em" }}>
            Tip Me
          </a>
        </div>
        <ContactCard userId={user.id} userName={user.displayName} showToast={showToast} />
        <div style={{ ...S.card, padding:"24px", border:"1px solid #fecaca" }}>
          <h2 style={{ fontSize:"14px", fontWeight:700, marginBottom:"6px", display:"flex", alignItems:"center", gap:"6px", color:"#dc2626" }}><Trash2 size={14} /> Delete Account</h2>
          <p style={{ fontSize:"13px", color:"#7a7060", marginBottom:"16px", lineHeight:1.5 }}>Permanently deletes your account, all trips, and all expenses. This cannot be undone.</p>
          {!confirmDelete ? (
            <button style={{ ...S.btnDanger, width:"auto" }} onClick={() => setConfirmDelete(true)}>
              <Trash2 size={14} /> Delete my account
            </button>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
              <p style={{ fontSize:"13px", color:"#dc2626", fontWeight:600 }}>Are you sure? All your data will be permanently lost.</p>
              <div style={{ display:"flex", gap:"10px" }}>
                <button style={{ ...S.btnDanger, flex:1 }} onClick={handleDeleteAccount} disabled={deleteLoading}>
                  {deleteLoading ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : <Trash2 size={14} />}
                  {deleteLoading ? "Deleting…" : "Yes, delete everything"}
                </button>
                <button style={{ ...S.btnGhost, flex:1 }} onClick={() => setConfirmDelete(false)}>Cancel</button>
              </div>
            </div>
          )}
        </div>
        <p style={{ textAlign:"center", fontSize:"11px", color:"#a39888", paddingBottom:"8px" }}>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ color:"#a39888", textDecoration:"none", borderBottom:"1px solid #d1c9bf" }}>Privacy Policy</a>
          <span style={{ margin:"0 8px" }}>·</span>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ color:"#a39888", textDecoration:"none", borderBottom:"1px solid #d1c9bf" }}>Terms of Service</a>
        </p>
      </div>
    </div>
  );
}

function TripCard({ trip, homeCurrency, convert, ratesLoading, onClick }) {
  const totalInTrip  = trip.expenses.reduce((s, e) => s + (convert(e.amount, e.currency) != null ? convert(e.amount, e.currency) / (convert(1, trip.currency) || 1) : 0), 0);
  const totalInHome  = trip.expenses.reduce((s, e) => { const v = convert(e.amount, e.currency); return v != null ? s + v : s; }, 0);
  const budgetInHome = trip.budget ? convert(trip.budget, trip.currency) : null;
  const pct    = budgetInHome ? Math.min(100, (totalInHome / budgetInHome) * 100) : null;
  const days   = Math.ceil((new Date(trip.endDate) - new Date(trip.startDate)) / 86400000) + 1;
  const col    = getTripColour(trip);
  const status = getTripStatus(trip);
  const ss     = STATUS_STYLE[status];

  return (
    <div style={{ ...S.card, cursor:"pointer", transition:"all .2s", overflow:"hidden" }} onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.11)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform=""; e.currentTarget.style.boxShadow=""; }}>
      <div style={{ height:6, background: col.bg }} />
      <div style={{ padding:"18px 20px 20px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"12px" }}>
          <div style={{ width:38, height:38, background: col.light, borderRadius:"10px", display:"flex", alignItems:"center", justifyContent:"center" }}>
            <MapPin size={17} color={col.bg} />
          </div>
          <div style={{ display:"flex", gap:"5px", alignItems:"center" }}>
            <span style={{ fontSize:"10px", background: ss.bg, color: ss.color, padding:"2px 8px", borderRadius:"999px", fontWeight:600 }}>{ss.label}</span>
            {trip.synced && <span style={{ fontSize:"10px", background:"#f0fdf4", color:"#166534", padding:"2px 6px", borderRadius:"999px", border:"1px solid #bbf7d0", display:"flex", alignItems:"center", gap:"2px" }}><BarChart3 size={8} /></span>}
          </div>
        </div>
        <h3 style={{ fontWeight:700, fontSize:"16px", marginBottom:"2px" }}>{trip.name}</h3>
        <p style={{ color:"#7a7060", fontSize:"12px", marginBottom:"14px", display:"flex", alignItems:"center", gap:"4px" }}><MapPin size={11} />{trip.destination} · {days}d</p>
        <div style={{ marginBottom:"10px" }}>
          <p style={{ fontFamily:"monospace", fontSize:"16px", fontWeight:700, marginBottom:"2px" }}>{fmt(totalInTrip, trip.currency)}</p>
          {homeCurrency !== trip.currency && (
            <p style={{ fontSize:"11px", color:"#a39888", display:"flex", alignItems:"center", gap:"3px" }}>
              {ratesLoading ? <Loader2 size={10} style={{ animation:"spin 1s linear infinite" }} /> : <span>≈</span>}
              {ratesLoading ? "…" : fmt(totalInHome, homeCurrency)}
            </p>
          )}
        </div>
        {pct !== null && (
          <div style={{ height:"3px", background:"#e5e0d8", borderRadius:"999px", overflow:"hidden", marginBottom:"10px" }}>
            <div style={{ height:"100%", width:pct+"%", background: pct>90?"#ef4444":pct>70?"#eab308": col.bg, borderRadius:"999px", transition:"width .5s" }} />
          </div>
        )}
        <p style={{ fontSize:"11px", color:"#a39888", display:"flex", alignItems:"center", gap:"4px" }}><CreditCard size={10} />{trip.expenses.length} expense{trip.expenses.length!==1?"s":""}</p>
      </div>
    </div>
  );
}

function TripDetail({ trip, user, updateTrips, showToast, onBack, onDeleteTrip, firstTrip }) {
  const [confirmDeleteTrip, setConfirmDeleteTrip] = useState(false);
  const [showAdd,   setShowAdd]   = useState(false);
  const [showEdit,  setShowEdit]  = useState(false);
  const [syncing,   setSyncing]   = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showTip,   setShowTip]   = useState(firstTrip || false);
  const { loading: ratesLoading, error: ratesError, lastUpdated, refresh, convert } = useRates(user.homeCurrency);

  const expenses = trip.expenses;
  const totalInTrip  = expenses.reduce((s, e) => { const v = convert(e.amount, e.currency); const r = convert(1, trip.currency); return v != null && r ? s + v / r : s; }, 0);
  const totalInHome  = expenses.reduce((s, e) => { const v = convert(e.amount, e.currency); return v != null ? s + v : s; }, 0);
  const budgetInHome = trip.budget ? convert(trip.budget, trip.currency) : null;
  const pct  = budgetInHome ? Math.min(100, (totalInHome / budgetInHome) * 100) : null;
  const days = Math.ceil((new Date(trip.endDate) - new Date(trip.startDate)) / 86400000) + 1;

  // ── CSV Export ──────────────────────────────────────────────
  function exportCSV() {
    const headers = ["Date", "Category", "Description", `Amount (${trip.currency})`, `Amount (${user.homeCurrency})`, "Notes"];
    const rows = [...expenses]
      .sort((a, b) => a.date.localeCompare(b.date))
      .map(e => {
        const homeVal = convert(e.amount, e.currency);
        return [
          e.date,
          e.category,
          `"${(e.description || "").replace(/"/g, '""')}"`,
          e.amount,
          homeVal != null ? homeVal.toFixed(currencyDp(user.homeCurrency)) : "",
          `"${(e.notes || "").replace(/"/g, '""')}"`,
        ];
      });
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${trip.name.replace(/[^a-z0-9]/gi, "_")}_expenses.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const grouped = {};
  [...expenses].sort((a, b) => b.date.localeCompare(a.date)).forEach(e => { if (!grouped[e.date]) grouped[e.date] = []; grouped[e.date].push(e); });

  const catTotals = CATEGORIES.map(c => {
    const exps = expenses.filter(e => e.category === c.value);
    const homeTotal = exps.reduce((s, e) => { const v = convert(e.amount, e.currency); return v != null ? s + v : s; }, 0);
    return { ...c, homeTotal };
  }).filter(c => c.homeTotal > 0).sort((a, b) => b.homeTotal - a.homeTotal);

  async function handleAdd(expense) {
    const { data, error } = await supabase.from("expenses").insert({
      trip_id: trip.id, category: expense.category, description: expense.description,
      amount: expense.amount, currency: expense.currency, date: expense.date, notes: expense.notes || null,
    }).select().single();
    if (error) { showToast("Could not save expense.", "error"); return; }
    const savedExpense = { ...expense, id: data.id };
    updateTrips(ts => ts.map(t => t.id === trip.id ? { ...t, expenses:[...t.expenses, savedExpense] } : t));
    setShowAdd(false); setShowTip(false); showToast("Expense added!");
  }
  async function handleDelete(id) {
    await supabase.from("expenses").delete().eq("id", id).eq("trip_id", trip.id);
    updateTrips(ts => ts.map(t => t.id === trip.id ? { ...t, expenses: t.expenses.filter(e => e.id !== id) } : t));
    showToast("Removed.");
  }
  async function handleUpdate(updated) {
    await supabase.from("expenses").update({ category: updated.category, description: updated.description, amount: updated.amount, currency: updated.currency, date: updated.date, notes: updated.notes || null }).eq("id", updated.id).eq("trip_id", trip.id);
    updateTrips(ts => ts.map(t => t.id === trip.id ? { ...t, expenses: t.expenses.map(e => e.id === updated.id ? updated : e) } : t));
  }
  async function handleSync() {
    if (!user.hasGoogle) { showToast("Connect Google Sheets in Settings first.", "error"); return; }
    setSyncing(true);
    try {
      let rates = {};
      try {
        const ratesRes = await fetch("https://api.frankfurter.dev/v1/latest?base=" + user.homeCurrency);
        const ratesData = await ratesRes.json();
        rates = { ...ratesData.rates, [user.homeCurrency]: 1 };
      } catch(e) {}
      const res = await fetch(EDGE_FN_URL + "/sync", {
        method: "POST",
        headers: EDGE_HEADERS,
        body: JSON.stringify({
          userId: user.id,
          trip: { id: trip.id, name: trip.name, destination: trip.destination, startDate: trip.startDate, endDate: trip.endDate, currency: trip.currency, budget: trip.budget || null, expenses: trip.expenses },
          homeCurrency: user.homeCurrency,
          rates,
        }),
      });
      const text = await res.text();
      let data = {};
      try { data = JSON.parse(text); } catch(e) { data = { error: text }; }
      if (data.success) {
        updateTrips(ts => ts.map(t => t.id === trip.id ? { ...t, synced: true } : t));
        if (data.spreadsheetId && !user.spreadsheetId) {
          await supabase.from("users").update({ spreadsheet_id: data.spreadsheetId }).eq("id", user.id);
        }
        showToast("Synced to Google Sheets! ✓");
      } else {
        showToast(data.error || "Sync failed.", "error");
      }
    } catch(e) {
      showToast("Sync failed — check your connection.", "error");
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div>
      <div style={S.topbar} className="topbar-mobile-gap">
        <button style={{ ...S.btnGhost, padding:"5px 8px" }} onClick={onBack}><ArrowLeft size={18} /></button>
        <div style={{ width:32, height:32, background:"#f0ede8", borderRadius:"8px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <MapPin size={16} color="#625a4d" />
        </div>
        <div style={{ flex:1, minWidth:0, display:"flex", alignItems:"center", gap:"8px" }}>
          <div style={{ minWidth:0 }}>
            <p style={{ fontWeight:700, fontSize:"15px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", margin:0, lineHeight:1.2 }}>{trip.name}</p>
            <p style={{ fontSize:"11px", color:"#7a7060", margin:0, lineHeight:1.2 }}>{trip.destination}</p>
          </div>
          <button onClick={() => setShowEdit(true)} style={{ ...S.btnGhost, padding:"5px 6px", flexShrink:0, color:"#a39888" }} title="Edit trip"><Settings size={14} /></button>
        </div>
        {ratesLoading && <Loader2 size={14} color="#a39888" style={{ animation:"spin 1s linear infinite", flexShrink:0 }} />}
        {lastUpdated && !ratesLoading && (
          <button onClick={refresh} className="desktop-only" style={{ ...S.btnGhost, fontSize:"11px", color:"#a39888", padding:"4px 8px" }}><RefreshCw size={11} /> Rates</button>
        )}
        {expenses.length > 0 && (
          <button style={{ ...S.btnSecondary, fontSize:"12px", padding:"7px 8px" }} onClick={exportCSV} title="Export CSV" className="topbar-btn-sm">
            <Download size={13} />
            <span className="desktop-only">Export CSV</span>
          </button>
        )}
        <button style={{ ...S.btnSecondary, fontSize:"12px", padding:"7px 8px" }} onClick={handleSync} disabled={syncing} className="topbar-btn-sm">
          {syncing ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <BarChart3 size={13} />}
          <span className="desktop-only">{syncing ? "Syncing…" : "Sync Sheets"}</span>
        </button>
        <button style={{ ...S.btnPrimary, width:"auto", padding:"8px 14px", fontSize:"13px" }} onClick={() => setShowAdd(true)}>
          <Plus size={14} /> Add
        </button>
      </div>
      {ratesError && <div style={{ background:"#fffbeb", borderBottom:"1px solid #fde68a", padding:"7px 20px", fontSize:"12px", color:"#92400e", display:"flex", alignItems:"center", gap:"6px" }}><AlertCircle size={12} /> {ratesError}</div>}
      {showTip && (
        <div style={{ background:"#f0f9ff", borderBottom:"1px solid #bae6fd", padding:"10px 20px", display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{ width:28, height:28, background:"#0ea5e9", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}><Plus size={14} color="white" /></div>
          <p style={{ fontSize:"13px", color:"#0c4a6e", flex:1, lineHeight:1.5 }}><strong>Your trip is ready.</strong> Tap <strong>Add</strong> to log your first expense.</p>
          <button onClick={() => setShowTip(false)} style={{ background:"none", border:"none", cursor:"pointer", color:"#7dd3fc", padding:"4px", flexShrink:0 }}><X size={15} /></button>
        </div>
      )}
      <div style={{ maxWidth:860, margin:"0 auto", padding:"12px clamp(10px, 3vw, 16px)", overflowX:"hidden", width:"100%", boxSizing:"border-box", minWidth:0 }}>
        <div className="stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px", marginBottom:"14px", minWidth:0, width:"100%" }}>
          {[
            { Icon: Calendar,   label:"Duration",    val: days + " days",  sub: new Date(trip.startDate+"T00:00:00").toLocaleDateString("en",{month:"short",day:"numeric"}) + " – " + new Date(trip.endDate+"T00:00:00").toLocaleDateString("en",{month:"short",day:"numeric",year:"numeric"}) },
            { Icon: DollarSign, label:"Total Spent", val: fmt(totalInTrip, trip.currency), sub: user.homeCurrency !== trip.currency ? "≈ " + fmt(totalInHome, user.homeCurrency) + " " + user.homeCurrency : expenses.length + " expense" + (expenses.length !== 1 ? "s" : "") },
            { Icon: TrendingUp, label:"Per Day",     val: fmt(totalInTrip / Math.max(days,1), trip.currency), sub: user.homeCurrency !== trip.currency ? "≈ " + fmt(totalInHome / Math.max(days,1), user.homeCurrency) + " " + user.homeCurrency : "daily avg" },
          ].map(s => (
            <div key={s.label} style={{ ...S.card, padding:"14px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"5px", color:"#a39888", fontSize:"11px", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"6px" }}><s.Icon size={11} /> {s.label}</div>
              <p style={{ fontWeight:700, fontSize:"clamp(13px, 4vw, 18px)", marginBottom:"2px", wordBreak:"break-word" }}>{s.val}</p>
              <p style={{ fontSize:"11px", color:"#a39888" }}>{s.sub}</p>
            </div>
          ))}
        </div>
        {pct !== null && (
          <div style={{ ...S.card, padding:"14px 16px", marginBottom:"14px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:"12px", color:"#7a7060", marginBottom:"7px" }}>
              <span style={{ display:"flex", alignItems:"center", gap:"4px" }}><Landmark size={12} /> Budget: {fmt(trip.budget, trip.currency)} {user.homeCurrency !== trip.currency && budgetInHome ? `(≈ ${fmt(budgetInHome, user.homeCurrency)})` : ""}</span>
              <span style={{ fontWeight:600 }}>{pct.toFixed(1)}% used</span>
            </div>
            <div style={{ height:"7px", background:"#e5e0d8", borderRadius:"999px", overflow:"hidden" }}>
              <div style={{ height:"100%", width:pct+"%", background: pct>90?"#ef4444":pct>70?"#eab308":"#22c55e", borderRadius:"999px", transition:"width .6s" }} />
            </div>
            <p style={{ fontSize:"11px", color: pct>100?"#dc2626":"#a39888", marginTop:"5px", textAlign:"right" }}>
              {pct > 100 ? fmt(totalInHome - budgetInHome, user.homeCurrency) + " over budget" : fmt(budgetInHome - totalInHome, user.homeCurrency) + " remaining"}
            </p>
          </div>
        )}
        <div className="trip-layout" style={{ display:"grid", gap:"16px", alignItems:"start", minWidth:0, width:"100%" }}>
          <div style={{ minWidth:0, width:"100%" }}>
            {expenses.length === 0 ? (
              <div style={{ ...S.card, padding:"48px", textAlign:"center" }}>
                <div style={{ width:52, height:52, background:"#f0f0ee", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}><CreditCard size={24} color="#a39888" /></div>
                <h3 style={{ fontWeight:600, marginBottom:"6px" }}>No expenses yet</h3>
                <p style={{ color:"#7a7060", fontSize:"13px" }}>Use the Add button above to log your first expense.</p>
              </div>
            ) : (
              Object.entries(grouped).map(([date, exps]) => (
                <div key={date} style={{ marginBottom:"20px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
                    <p style={{ fontSize:"11px", fontWeight:600, color:"#7a7060", textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap" }}>
                      {new Date(date+"T00:00:00").toLocaleDateString("en",{weekday:"short",month:"short",day:"numeric"})}
                    </p>
                    <div style={{ flex:1, height:"1px", background:"#e5e0d8" }} />
                    <p style={{ fontSize:"11px", fontFamily:"monospace", color:"#a39888", whiteSpace:"nowrap" }}>{fmt(exps.reduce((s,e) => { const inHome = convert(e.amount, e.currency); const tripRate = convert(1, trip.currency); return inHome != null && tripRate ? s + inHome / tripRate : s + (e.currency === trip.currency ? e.amount : 0); }, 0), trip.currency)}</p>
                  </div>
                  <div style={{ ...S.card, overflow:"hidden" }}>
                    {exps.map((exp, i) => (
                      <ExpenseRow key={exp.id} exp={exp} isLast={i === exps.length - 1} homeCurrency={user.homeCurrency} convert={convert} onUpdate={handleUpdate} onDelete={handleDelete} editingId={editingId} setEditingId={setEditingId} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:"12px", position:"sticky", top:"68px", minWidth:0 }}>
            {catTotals.length > 0 && (
              <BreakdownCard catTotals={catTotals} totalInHome={totalInHome} homeCurrency={user.homeCurrency} ratesLoading={ratesLoading} />
            )}
            {expenses.length > 0 && (
                <div style={{ ...S.card, padding:"16px" }}>
                  <h3 style={{ fontWeight:700, fontSize:"13px", marginBottom:"10px", display:"flex", alignItems:"center", gap:"5px" }}><BarChart3 size={13} /> Google Sheets</h3>
                  {trip.synced ? (
                    <>
                      <p style={{ fontSize:"12px", color:"#16a34a", marginBottom:"4px", display:"flex", alignItems:"center", gap:"4px" }}><CheckCircle size={12} /> Synced to sheet</p>
                      <p style={{ fontSize:"11px", color:"#a39888", marginBottom:"8px" }}>Tab: "{trip.name}"</p>
                      {user.spreadsheetId && (
                        <a href={"https://docs.google.com/spreadsheets/d/" + user.spreadsheetId} target="_blank" rel="noopener noreferrer"
                          style={{ ...S.btnSecondary, width:"100%", fontSize:"12px", marginBottom:"6px", textDecoration:"none" }}>
                          <ExternalLink size={12} /> Open in Sheets
                        </a>
                      )}
                      <button style={{ ...S.btnSecondary, width:"100%", fontSize:"12px" }} onClick={handleSync} disabled={syncing}>
                        {syncing ? <Loader2 size={12} style={{ animation:"spin 1s linear infinite" }} /> : <RefreshCw size={12} />}
                        {syncing ? "Syncing…" : "Re-sync tab"}
                      </button>
                    </>
                  ) : (
                    <>
                      <p style={{ fontSize:"12px", color:"#7a7060", marginBottom:"10px", lineHeight:1.5 }}>
                        {user.hasGoogle ? (user.spreadsheetId ? `Adds a "${trip.name}" tab to your existing SÉJOUR sheet.` : `Creates a "SÉJOUR — ${user.displayName}" sheet with a tab for this trip.`) : "Connect Google Sheets in settings first."}
                      </p>
                      <button style={{ ...S.btnSecondary, width:"100%", fontSize:"12px" }} onClick={handleSync} disabled={!user.hasGoogle||syncing}>
                        {syncing ? <Loader2 size={12} style={{ animation:"spin 1s linear infinite" }} /> : <BarChart3 size={12} />}
                        {syncing ? "Syncing…" : user.spreadsheetId ? "Add tab to sheet" : "Create sheet"}
                      </button>
                    </>
                  )}
                </div>
            )}
            {!confirmDeleteTrip ? (
              <button onClick={() => setConfirmDeleteTrip(true)} style={{ ...S.btnDanger, width:"100%", fontSize:"12px" }}><Trash2 size={13} /> Delete trip</button>
            ) : (
              <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:"10px", padding:"12px", display:"flex", flexDirection:"column", gap:"8px" }}>
                <p style={{ fontSize:"12px", color:"#dc2626", fontWeight:600, margin:0, textAlign:"center" }}>Delete "{trip.name}"?</p>
                <p style={{ fontSize:"11px", color:"#a39888", margin:0, textAlign:"center" }}>This cannot be undone.</p>
                <div style={{ display:"flex", gap:"6px" }}>
                  <button onClick={() => setConfirmDeleteTrip(false)} style={{ ...S.btnSecondary, flex:1, fontSize:"12px", padding:"7px" }}>Cancel</button>
                  <button onClick={onDeleteTrip} style={{ ...S.btnDanger, flex:1, fontSize:"12px", padding:"7px", background:"#dc2626", color:"#fff", borderColor:"#dc2626" }}><Trash2 size={12} /> Delete</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showAdd && <AddExpenseModal tripId={trip.id} defaultCurrency={trip.currency} homeCurrency={user.homeCurrency} convert={convert} ratesLoading={ratesLoading} onClose={() => setShowAdd(false)} onAdd={handleAdd} />}
      {showEdit && <EditTripModal trip={trip} onClose={() => setShowEdit(false)} homeCurrency={user.homeCurrency} convert={convert} ratesLoading={ratesLoading} onSave={async updated => {
        await supabase.from("trips").update({ name: updated.name, destination: updated.destination, start_date: updated.startDate, end_date: updated.endDate, currency: updated.currency, budget: updated.budget || null, colour: updated.colour }).eq("id", trip.id);
        updateTrips(ts => ts.map(t => t.id === trip.id ? { ...t, ...updated } : t));
        setShowEdit(false); showToast("Trip updated!");
      }} />}
    </div>
  );
}

function EditTripModal({ trip, onClose, onSave, homeCurrency, convert, ratesLoading }) {
  const [form, setForm] = useState({ name: trip.name, destination: trip.destination, startDate: trip.startDate, endDate: trip.endDate, currency: trip.currency, budget: trip.budget ? String(trip.budget) : "", colour: trip.colour || "teal" });
  const [error, setError] = useState("");
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  function handleSave() {
    if (!form.name.trim() || !form.destination.trim()) { setError("Name and destination are required."); return; }
    if (!form.endDate || form.endDate < form.startDate) { setError("End date must be after start date."); return; }
    onSave({ ...form, budget: form.budget ? parseFloat(form.budget) : undefined });
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div className="modal-sheet" style={{ ...S.card, width:"100%", maxWidth:"480px", maxHeight:"90vh", overflowY:"auto", overflowX:"hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:"20px 20px 0", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <h2 style={{ fontSize:"18px", fontWeight:700, display:"flex", alignItems:"center", gap:"8px" }}><Settings size={17} /> Edit Trip</h2>
          <button type="button" style={S.btnGhost} onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding:"0 20px 20px", display:"flex", flexDirection:"column", gap:"14px" }}>
          <div>
            <label style={S.label}>Colour</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(10, 1fr)", gap:"6px" }}>
              {TRIP_COLOURS.map(c => (
                <button type="button" key={c.id} onClick={() => f("colour", c.id)} title={c.label}
                  style={{ width:28, height:28, borderRadius:"50%", background:c.bg, border: form.colour===c.id ? "3px solid #1a1714" : "3px solid transparent", cursor:"pointer", transition:"transform .15s", outline:"none", padding:0, flexShrink:0, boxSizing:"border-box" }}
                  onMouseEnter={e => e.currentTarget.style.transform="scale(1.15)"}
                  onMouseLeave={e => e.currentTarget.style.transform=""} />
              ))}
            </div>
          </div>
          <div><label style={S.label}>Trip Name</label><input style={S.input} value={form.name} onChange={e => f("name", e.target.value)} autoFocus /></div>
          <div><label style={S.label}>Destination</label><input style={S.input} value={form.destination} onChange={e => f("destination", e.target.value)} /></div>
          <div className="g2">
            <div><label style={S.label}>Start Date</label><input type="date" style={S.input} value={form.startDate} onChange={e => f("startDate", e.target.value)} /></div>
            <div><label style={S.label}>End Date</label><input type="date" style={S.input} value={form.endDate} min={form.startDate} onChange={e => f("endDate", e.target.value)} /></div>
          </div>
          <div className="g2">
            <div>
              <label style={S.label}>Currency</label>
              <select style={S.input} value={form.currency} onChange={e => f("currency", e.target.value)}>
                {CURRENCIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Budget (optional)</label>
              <input type="number" style={S.input} value={form.budget} onChange={e => f("budget", e.target.value)} placeholder="0" min="0" />
              {form.budget && !isNaN(parseFloat(form.budget)) && form.currency !== homeCurrency && (() => {
                const preview = convert(parseFloat(form.budget), form.currency);
                return preview != null ? (
                  <p style={{ fontSize:"11px", color:"#a39888", marginTop:"4px", display:"flex", alignItems:"center", gap:"3px" }}>
                    {ratesLoading ? <Loader2 size={10} style={{ animation:"spin 1s linear infinite" }} /> : "≈"}
                    {ratesLoading ? "…" : fmt(preview, homeCurrency)} {homeCurrency}
                  </p>
                ) : null;
              })()}
            </div>
          </div>
          {error && <p style={{ color:"#dc2626", fontSize:"13px", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={13} /> {error}</p>}
          <div className="g2" style={{ paddingTop:"4px" }}>
            <button style={S.btnSecondary} onClick={onClose}>Cancel</button>
            <button style={S.btnPrimary} onClick={handleSave}><CheckCircle size={14} /> Save changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function BreakdownCard({ catTotals, totalInHome, homeCurrency, ratesLoading }) {
  const [view, setView] = useState("bars");
  const PIE_SIZE = 160;
  const cx = PIE_SIZE / 2, cy = PIE_SIZE / 2, r = 62, innerR = 36;

  function polarToXY(angleDeg, radius) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  }

  function buildSlices() {
    let startAngle = 0;
    return catTotals.map(c => {
      const pct = totalInHome > 0 ? c.homeTotal / totalInHome : 0;
      const sweep = pct * 360;
      const endAngle = startAngle + sweep;
      const largeArc = sweep > 180 ? 1 : 0;
      const p1 = polarToXY(startAngle, r), p2 = polarToXY(endAngle, r);
      const i1 = polarToXY(startAngle, innerR), i2 = polarToXY(endAngle, innerR);
      const path = sweep < 0.5 ? null : `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArc} 1 ${p2.x} ${p2.y} L ${i2.x} ${i2.y} A ${innerR} ${innerR} 0 ${largeArc} 0 ${i1.x} ${i1.y} Z`;
      const result = { ...c, path, pct, startAngle, endAngle };
      startAngle = endAngle;
      return result;
    });
  }

  const PIE_COLORS = { food:"#d96b5a", accommodation:"#e8935a", transport:"#d4a832", activities:"#7aab72", shopping:"#2eaa96", health:"#48a0cc", beauty:"#4060c0", visa:"#9b84cc", insurance:"#a05c8a", other:"#e07b8a" };
  const slices = buildSlices();

  return (
    <div style={{ ...S.card, padding:"16px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"14px" }}>
        <h3 style={{ fontWeight:700, fontSize:"13px", display:"flex", alignItems:"center", gap:"5px" }}><BarChart3 size={13} /> Breakdown ({homeCurrency})</h3>
        <div style={{ display:"flex", gap:"3px", background:"#f4f2ef", padding:"3px", borderRadius:"8px" }}>
          {[["bars", <BarChart3 size={11} />], ["pie", <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 1 10 10h-10z"/><circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="2"/></svg>]].map(([v, icon]) => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding:"4px 8px", borderRadius:"5px", border:"none", fontSize:"11px", cursor:"pointer", fontFamily:"inherit", fontWeight:500, background: view===v ? "white" : "transparent", color: view===v ? "#1a1714" : "#a39888", boxShadow: view===v ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
              {icon}
            </button>
          ))}
        </div>
      </div>
      {view === "bars" ? (
        <div>
          {catTotals.map(c => {
            const CatIcon = c.Icon;
            return (
              <div key={c.value} style={{ marginBottom:"10px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:"12px", marginBottom:"4px", alignItems:"center" }}>
                  <span style={{ display:"flex", alignItems:"center", gap:"5px" }}>
                    <div style={{ width:20, height:20, borderRadius:"5px", background:CAT_COLORS[c.value], display:"flex", alignItems:"center", justifyContent:"center" }}><CatIcon size={11} color={CAT_TEXT[c.value]} /></div>
                    {c.label}
                  </span>
                  <span style={{ fontFamily:"monospace", fontWeight:600 }}>{fmtCompact(c.homeTotal, homeCurrency)}</span>
                </div>
                <div style={{ height:"3px", background:"#e5e0d8", borderRadius:"999px", overflow:"hidden" }}>
                  <div style={{ height:"100%", width:((c.homeTotal/totalInHome)*100)+"%", background:CAT_TEXT[c.value]||"#a39888", borderRadius:"999px", transition:"width .4s" }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          <svg width="100%" viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`} style={{ display:"block", margin:"0 auto 12px" }}>
            {slices.map((s, i) => s.path && <path key={s.value} d={s.path} fill={PIE_COLORS[s.value] || "#a39888"} stroke="white" strokeWidth="1.5" />)}
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="10" fill="#7a7060" fontFamily="inherit">Total</text>
            <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="#1a1714" fontFamily="monospace" fontWeight="bold">{fmtCompact(totalInHome, homeCurrency)}</text>
          </svg>
          <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
            {slices.map((s, i) => (
              <div key={s.value} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:"11px" }}>
                <span style={{ display:"flex", alignItems:"center", gap:"5px" }}>
                  <span style={{ width:9, height:9, borderRadius:"2px", background: PIE_COLORS[s.value] || "#a39888", display:"inline-block", flexShrink:0 }} />{s.label}
                </span>
                <span style={{ fontFamily:"monospace", color:"#625a4d" }}>{(s.pct * 100).toFixed(0)}% · {fmtCompact(s.homeTotal, homeCurrency)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {ratesLoading && <p style={{ fontSize:"11px", color:"#a39888", display:"flex", alignItems:"center", gap:"4px", marginTop:"8px" }}><Loader2 size={10} style={{ animation:"spin 1s linear infinite" }} /> Updating rates…</p>}
    </div>
  );
}

function ExpenseRow({ exp, isLast, homeCurrency, convert, onUpdate, onDelete, editingId, setEditingId }) {
  const editing = editingId === exp.id;
  const [draft, setDraft] = useState(null);
  const homeVal = convert(exp.amount, exp.currency);

  function startEdit() { setDraft({ ...exp, amount: String(exp.amount) }); setEditingId(exp.id); }
  function cancelEdit() { setEditingId(null); setDraft(null); }
  function saveEdit() {
    const amt = parseFloat(draft.amount);
    if (!draft.description.trim() || isNaN(amt) || amt <= 0) return;
    onUpdate({ ...draft, amount: amt }); setEditingId(null); setDraft(null);
  }
  function d(k, v) { setDraft(p => ({ ...p, [k]: v })); }

  const previewHome = draft && !isNaN(parseFloat(draft.amount)) ? convert(parseFloat(draft.amount), draft.currency || exp.currency) : null;
  const cat = CATEGORIES.find(c => c.value === exp.category) || CATEGORIES[CATEGORIES.length - 1];
  const CatIcon = cat.Icon;
  const rowBorder = isLast ? "none" : "1px solid #f0ede8";

  if (editing) {
    return (
      <div style={{ padding:"14px", borderBottom: rowBorder, background:"#fdfcfb" }}>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(10,1fr)", gap:"4px", marginBottom:"12px" }}>
          {CATEGORIES.map(c => {
            const CI = c.Icon; const sel = draft.category === c.value;
            return (
              <button key={c.value} onClick={() => d("category", c.value)} title={c.label}
                style={{ display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", borderRadius:"8px", border:"2px solid", borderColor: sel ? "#1a1714" : "#e5e0d8", background: sel ? "#1a1714" : CAT_COLORS[c.value] || "#f0ede8", boxSizing:"border-box", width:"100%", height:"32px", minWidth:0 }}>
                <CI size={13} color={sel ? "#f5f0e8" : CAT_TEXT[c.value] || "#625a4d"} />
              </button>
            );
          })}
        </div>
        <div style={{ marginBottom:"8px" }}>
          <label style={S.label}>Description</label>
          <input style={{ ...S.input }} value={draft.description} onChange={e => d("description", e.target.value)} onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }} autoFocus />
        </div>
        <div className="g2" style={{ marginBottom:"8px" }}>
          <div>
            <label style={S.label}>Amount</label>
            <input type="number" style={{ ...S.input, fontFamily:"monospace" }} value={draft.amount} onChange={e => d("amount", e.target.value)} onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }} step="0.01" min="0" />
            {previewHome != null && draft.currency !== homeCurrency && <p style={{ fontSize:"10px", color:"#a39888", marginTop:"3px" }}>≈ {fmt(previewHome, homeCurrency)}</p>}
          </div>
          <div>
            <label style={S.label}>Currency</label>
            <select style={{ ...S.input }} value={draft.currency} onChange={e => d("currency", e.target.value)}>
              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div style={{ marginBottom:"8px" }}>
          <label style={S.label}>Date</label>
          <input type="date" style={{ ...S.input }} value={draft.date} onChange={e => d("date", e.target.value)} />
        </div>
        <div style={{ marginBottom:"10px" }}>
          <label style={S.label}>Notes (optional)</label>
          <input style={{ ...S.input }} value={draft.notes} onChange={e => d("notes", e.target.value)} onKeyDown={e => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") cancelEdit(); }}  />
        </div>
        <div style={{ display:"flex", gap:"8px", justifyContent:"flex-end" }}>
          <button onClick={cancelEdit} style={{ ...S.btnGhost, fontSize:"12px", padding:"6px 12px" }}><X size={13} /> Cancel</button>
          <button onClick={() => onDelete(exp.id)} style={{ ...S.btnDanger, fontSize:"12px", padding:"6px 12px" }}><Trash2 size={13} /> Delete</button>
          <button onClick={saveEdit} style={{ ...S.btnPrimary, width:"auto", fontSize:"12px", padding:"6px 14px" }}><CheckCircle size={13} /> Save</button>
        </div>
      </div>
    );
  }

  return (
    <div onClick={startEdit} style={{ display:"flex", alignItems:"center", gap:"12px", padding:"12px 14px", borderBottom: rowBorder, cursor:"pointer", transition:"background .12s" }}
      onMouseEnter={e => e.currentTarget.style.background="#faf9f7"} onMouseLeave={e => e.currentTarget.style.background=""}>
      <div style={{ width:34, height:34, borderRadius:"9px", background: CAT_COLORS[exp.category]||"#f0ede8", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <CatIcon size={15} color={CAT_TEXT[exp.category]||"#625a4d"} />
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:"8px" }}>
          <p style={{ fontSize:"14px", fontWeight:500, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", margin:0, flex:1, minWidth:0 }}>{exp.description}</p>
          <p style={{ fontFamily:"monospace", fontSize:"13px", fontWeight:700, margin:0, flexShrink:0 }}>{fmt(exp.amount, exp.currency)}</p>
        </div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:"6px", marginTop:"3px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"6px", minWidth:0 }}>
            <span style={{ fontSize:"10px", fontWeight:600, color: CAT_TEXT[exp.category], background: CAT_COLORS[exp.category], padding:"1px 6px", borderRadius:"4px", flexShrink:0 }}>{cat.label}</span>
            {exp.notes && <span style={{ fontSize:"11px", color:"#a39888", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{exp.notes}</span>}
          </div>
          {homeCurrency !== exp.currency && homeVal != null
            ? <p style={{ fontSize:"10px", color:"#a39888", fontFamily:"monospace", margin:0, flexShrink:0 }}>≈ {fmt(homeVal, homeCurrency)}</p>
            : <span style={{ fontSize:"10px", color:"#c8bfb5" }}>{new Date(exp.date+"T00:00:00").toLocaleDateString("en",{month:"short",day:"numeric"})}</span>}
        </div>
      </div>
      <div style={{ color:"#d1c9bf", flexShrink:0, display:"flex", alignItems:"center" }}><Settings size={13} /></div>
    </div>
  );
}

function NewTripModal({ onClose, onCreate, defaultCurrency, homeCurrency, convert, ratesLoading }) {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ name:"", destination:"", startDate:today, endDate:"", currency:defaultCurrency||"USD", budget:"", colour:"teal" });
  const [error, setError] = useState("");
  const f = (k, v) => setForm(p => ({ ...p, [k]:v }));

  function handleSubmit() {
    if (!form.name || !form.destination || !form.endDate) { setError("Please fill all required fields."); return; }
    onCreate({ id:uid(), ...form, budget: form.budget ? parseFloat(form.budget) : undefined, expenses:[], synced: false });
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div className="modal-sheet" style={{ ...S.card, width:"100%", maxWidth:"480px", maxHeight:"90vh", overflowY:"auto", overflowX:"hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:"20px 20px 0", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <h2 style={{ fontSize:"18px", fontWeight:700, display:"flex", alignItems:"center", gap:"8px" }}><MapPin size={18} /> New Trip</h2>
          <button type="button" style={S.btnGhost} onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding:"0 20px 20px", display:"flex", flexDirection:"column", gap:"14px" }}>
          <div>
            <label style={S.label}>Colour</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(10, 1fr)", gap:"6px" }}>
              {TRIP_COLOURS.map(c => (
                <button type="button" key={c.id} onClick={() => f("colour", c.id)} title={c.label}
                  style={{ width:28, height:28, borderRadius:"50%", background:c.bg, border: form.colour===c.id ? "3px solid #1a1714" : "3px solid transparent", cursor:"pointer", transition:"transform .15s", outline:"none", padding:0, flexShrink:0, boxSizing:"border-box" }}
                  onMouseEnter={e => e.currentTarget.style.transform="scale(1.15)"}
                  onMouseLeave={e => e.currentTarget.style.transform=""} />
              ))}
            </div>
          </div>
          <div><label style={S.label}>Trip Name *</label><input style={S.input} value={form.name} onChange={e=>f("name",e.target.value)} autoFocus /></div>
          <div><label style={S.label}>Destination *</label><input style={S.input} value={form.destination} onChange={e=>f("destination",e.target.value)} placeholder="e.g. Tokyo, Japan" /></div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
            <div><label style={S.label}>Start *</label><input type="date" style={S.input} value={form.startDate} onChange={e=>f("startDate",e.target.value)} /></div>
            <div><label style={S.label}>End *</label><input type="date" style={S.input} value={form.endDate} min={form.startDate} onChange={e=>f("endDate",e.target.value)} /></div>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
            <div>
              <label style={S.label}>Trip Currency</label>
              <select style={S.input} value={form.currency} onChange={e=>f("currency",e.target.value)}>
                {CURRENCIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>Budget (optional)</label>
              <input type="number" style={S.input} value={form.budget} onChange={e=>f("budget",e.target.value)} placeholder="0" />
              {form.budget && !isNaN(parseFloat(form.budget)) && form.currency !== homeCurrency && (() => {
                const preview = convert(parseFloat(form.budget), form.currency);
                return preview != null ? (
                  <p style={{ fontSize:"11px", color:"#a39888", marginTop:"4px", display:"flex", alignItems:"center", gap:"3px" }}>
                    {ratesLoading ? <Loader2 size={10} style={{ animation:"spin 1s linear infinite" }} /> : "≈"}
                    {ratesLoading ? "…" : fmt(preview, homeCurrency)} {homeCurrency}
                  </p>
                ) : null;
              })()}
            </div>
          </div>
          {error && <p style={{ color:"#dc2626", fontSize:"13px", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={13}/>{error}</p>}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px", marginTop:"8px" }}>
            <button type="button" style={S.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="button" style={S.btnPrimary} onClick={handleSubmit}><Plus size={14}/> Create Trip</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddExpenseModal({ tripId, defaultCurrency, homeCurrency, convert, ratesLoading, onClose, onAdd }) {
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({ date:today, category:"food", description:"", amount:"", currency:defaultCurrency, notes:"" });
  const [error, setError] = useState("");
  const f = (k, v) => setForm(p => ({ ...p, [k]:v }));

  const previewHome = form.amount && !isNaN(parseFloat(form.amount)) ? convert(parseFloat(form.amount), form.currency) : null;

  function handleSubmit() {
    if (!form.description || !form.amount) { setError("Description and amount are required."); return; }
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0) { setError("Enter a valid amount."); return; }
    onAdd({ id:uid(), tripId, ...form, amount:amt, createdAt:new Date().toISOString() });
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div className="modal-sheet" style={{ ...S.card, width:"100%", maxWidth:"480px", maxHeight:"90vh", overflowY:"auto", overflowX:"hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ padding:"20px 20px 0", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
          <h2 style={{ fontSize:"18px", fontWeight:700, display:"flex", alignItems:"center", gap:"8px" }}><CreditCard size={18}/> Add Expense</h2>
          <button type="button" style={S.btnGhost} onClick={onClose}><X size={16}/></button>
        </div>
        <div style={{ padding:"0 20px 20px", display:"flex", flexDirection:"column", gap:"14px" }}>
          <div>
            <label style={S.label}>Category</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:"6px" }}>
              {CATEGORIES.map(c => {
                const CatIcon = c.Icon; const sel = form.category === c.value;
                return (
                  <button type="button" key={c.value} onClick={()=>f("category",c.value)}
                    style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"4px", height:"56px", borderRadius:"10px", border:"2px solid", borderColor: sel ? "#1a1714" : CAT_TEXT[c.value], background: sel ? "#1a1714" : CAT_COLORS[c.value], color: sel ? "#f5f0e8" : CAT_TEXT[c.value], cursor:"pointer", fontSize:"9px", fontFamily:"inherit", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.03em", boxSizing:"border-box", width:"100%", minWidth:0 }}>
                    <CatIcon size={15} color={sel ? "#f5f0e8" : CAT_TEXT[c.value]} />{c.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div><label style={S.label}>Description *</label><input style={S.input} value={form.description} onChange={e=>f("description",e.target.value)} placeholder="e.g. Ramen at Ichiran" autoFocus /></div>
          <div className="g2">
            <div>
              <label style={S.label}>Amount *</label>
              <input type="number" style={{ ...S.input, fontFamily:"monospace" }} value={form.amount} onChange={e=>f("amount",e.target.value)} placeholder="0.00" step="0.01" />
              {previewHome != null && form.currency !== homeCurrency && (
                <p style={{ fontSize:"11px", color:"#a39888", marginTop:"4px", display:"flex", alignItems:"center", gap:"3px" }}>
                  {ratesLoading ? <Loader2 size={10} style={{ animation:"spin 1s linear infinite" }} /> : "≈"}
                  {ratesLoading ? "…" : fmt(previewHome, homeCurrency)} {homeCurrency}
                </p>
              )}
            </div>
            <div>
              <label style={S.label}>Currency</label>
              <select style={S.input} value={form.currency} onChange={e=>f("currency",e.target.value)}>
                {CURRENCIES.map(c=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label style={S.label}>Date</label><input type="date" style={S.input} value={form.date} onChange={e=>f("date",e.target.value)} /></div>
          <div><label style={S.label}>Notes (optional)</label><input style={S.input} value={form.notes} onChange={e=>f("notes",e.target.value)}  /></div>
          {error && <p style={{ color:"#dc2626", fontSize:"13px", display:"flex", alignItems:"center", gap:"4px" }}><AlertCircle size={13}/>{error}</p>}
          <div className="g2">
            <button type="button" style={S.btnSecondary} onClick={onClose}>Cancel</button>
            <button type="button" style={S.btnPrimary} onClick={handleSubmit}><Plus size={14}/> Add Expense</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Mode Pick Screen ──────────────────────────────────────────
function ModePickScreen({ user, onPick }) {
  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px", background:"#f8f7f4" }}>
      <div style={{ textAlign:"center", marginBottom:"32px" }}>
        <div style={{ width:52, height:52, background:"#1a1714", borderRadius:"14px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}>
          <Plane size={26} color="#f5f0e8" />
        </div>
        <h1 style={{ fontSize:"28px", fontWeight:700, marginBottom:"8px", letterSpacing:"0.2em" }}>SÉJOUR</h1>
        <p style={{ color:"#7a7060", fontSize:"14px" }}>Hi {user.displayName}, how would you like to start?</p>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"12px", width:"100%", maxWidth:"420px" }}>
        <button onClick={() => onPick("travel")} style={{ background:"white", border:"2px solid #e5e0d8", borderRadius:"16px", padding:"28px 16px", cursor:"pointer", fontFamily:"inherit", textAlign:"center", transition:"all .15s" }}
          onMouseOver={e => e.currentTarget.style.borderColor="#1a1714"} onMouseOut={e => e.currentTarget.style.borderColor="#e5e0d8"}>
          <div style={{ width:44, height:44, background:"#1a1714", borderRadius:"12px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}>
            <Plane size={22} color="#f5f0e8" />
          </div>
          <p style={{ fontWeight:700, fontSize:"15px", marginBottom:"4px", color:"#1a1714" }}>Travel</p>
          <p style={{ fontSize:"12px", color:"#7a7060", lineHeight:1.5 }}>Track expenses across trips and currencies</p>
        </button>
        <button onClick={() => onPick("home")} style={{ background:"white", border:"2px solid #e5e0d8", borderRadius:"16px", padding:"28px 16px", cursor:"pointer", fontFamily:"inherit", textAlign:"center", transition:"all .15s" }}
          onMouseOver={e => e.currentTarget.style.borderColor="#1a1714"} onMouseOut={e => e.currentTarget.style.borderColor="#e5e0d8"}>
          <div style={{ width:44, height:44, background:"#f4f2ef", borderRadius:"12px", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 12px" }}>
            <Home size={22} color="#1a1714" />
          </div>
          <p style={{ fontWeight:700, fontSize:"15px", marginBottom:"4px", color:"#1a1714" }}>Home</p>
          <p style={{ fontSize:"12px", color:"#7a7060", lineHeight:1.5 }}>Track your monthly living expenses</p>
        </button>
      </div>
    </div>
  );
}

// ── Travel Onboarding Screen ──────────────────────────────────
function TravelOnboardingScreen({ user, onAddTrip, onSkip }) {
  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"16px" }}>
      <div style={{ ...S.card, width:"100%", maxWidth:"420px", padding:"clamp(20px, 5vw, 32px)" }}>
        <div style={{ marginBottom:"24px" }}>
          <p style={{ fontSize:"13px", color:"#a39888", marginBottom:"6px" }}>You're all set</p>
          <h2 style={{ fontSize:"26px", fontWeight:700, marginBottom:"8px", lineHeight:1.2 }}>Ready to track your first trip?</h2>
          <p style={{ color:"#7a7060", fontSize:"14px", lineHeight:1.6 }}>Add a trip and start logging expenses as you go — or head to your dashboard and set one up later.</p>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
          <button style={{ ...S.btnPrimary, width:"100%", padding:"13px", fontSize:"14px" }} onClick={onAddTrip}>
            <Plus size={15} /> Add my first trip
          </button>
          <button style={{ ...S.btnSecondary, width:"100%", padding:"12px", fontSize:"13px" }} onClick={onSkip}>
            Go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Home Onboarding Screen ────────────────────────────────────
function HomeOnboardingScreen({ user, updateUser, onDone }) {
  const [budget, setBudget] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleDone() {
    setSaving(true);
    if (budget && !isNaN(parseFloat(budget))) {
      await supabase.from("users").update({ monthly_budget: parseFloat(budget) }).eq("id", user.id);
      updateUser(u => ({ ...u, monthlyBudget: parseFloat(budget) }));
    }
    setSaving(false);
    onDone();
  }

  return (
    <div style={{ minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"16px" }}>
      <div style={{ ...S.card, width:"100%", maxWidth:"420px", padding:"clamp(20px, 5vw, 32px)" }}>
        <div style={{ width:44, height:44, background:"#f4f2ef", borderRadius:"12px", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:"16px" }}>
          <Home size={22} color="#1a1714" />
        </div>
        <h2 style={{ fontSize:"24px", fontWeight:700, marginBottom:"8px" }}>Set up Home mode</h2>
        <p style={{ color:"#7a7060", fontSize:"14px", lineHeight:1.6, marginBottom:"24px" }}>Track your monthly living expenses in {user.homeCurrency}. Want to set a monthly budget?</p>
        <div style={{ marginBottom:"20px" }}>
          <label style={S.label}>Monthly Budget <span style={{ color:"#a39888", fontWeight:400 }}>(optional)</span></label>
          <div style={{ position:"relative" }}>
            <span style={{ position:"absolute", left:"12px", top:"50%", transform:"translateY(-50%)", fontSize:"13px", color:"#7a7060", fontWeight:500 }}>{user.homeCurrency}</span>
            <input style={{ ...S.input, paddingLeft:"48px" }} type="number" min="0" placeholder="0.00"
              value={budget} onChange={e => setBudget(e.target.value)} onKeyDown={e => e.key === "Enter" && handleDone()} />
          </div>
        </div>
        <button style={{ ...S.btnPrimary, width:"100%" }} onClick={handleDone} disabled={saving}>
          {saving ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : null}
          {saving ? "Saving…" : "Get started"}
        </button>
      </div>
    </div>
  );
}

// ── Home Dashboard ────────────────────────────────────────────
function HomeDashboard({ user, updateUser, monthlyExpenses, setMonthlyExpenses, allMonthlyData, setAllMonthlyData, monthlyBudgets, setMonthlyBudgets, homeView, setHomeView, currentMonth, onChangeMonth, onLoadAllData, mode, switchMode, onLogout, onGoSettings, showToast }) {
  const [showAdd,       setShowAdd]       = useState(false);
  const [editingExp,    setEditingExp]    = useState(null);
  const [chartStartIdx, setChartStartIdx] = useState(6); // index into last12Months where 6-month window starts
  const [chartFilter,   setChartFilter]   = useState("all");
  const [syncingSheets, setSyncingSheets] = useState(false);

  const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const SHORT_MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  function fmt(n) { return (n||0).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 }); }

  // ── Derived values for selected month ─────────────────────
  const total       = monthlyExpenses.reduce((s, e) => s + e.amount, 0);
  const budgetKey   = `${currentMonth.year}-${String(currentMonth.month+1).padStart(2,"0")}`;
  const budget      = monthlyBudgets[budgetKey] || null;
  const budgetPct   = budget ? Math.min(100, (total / budget) * 100) : null;
  const budgetOver  = budget && total > budget;
  const daysInMonth = new Date(currentMonth.year, currentMonth.month + 1, 0).getDate();
  const today       = new Date();
  const isThisMonth = today.getFullYear() === currentMonth.year && today.getMonth() === currentMonth.month;
  const daysLeft    = isThisMonth ? daysInMonth - today.getDate() : 0;
  const dailyAvg    = isThisMonth && today.getDate() > 0 ? total / today.getDate() : total > 0 ? total / daysInMonth : 0;
  const monthLabel  = `${MONTH_NAMES[currentMonth.month]} ${currentMonth.year}`;

  // ── Category totals for selected month ────────────────────
  const catTotals = HOME_CATEGORIES.map(c => ({
    ...c,
    total: monthlyExpenses.filter(e => e.category === c.value).reduce((s, e) => s + e.amount, 0),
  })).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

  // ── Grouped expenses by date ───────────────────────────────
  const byDate = {};
  monthlyExpenses.forEach(e => { if (!byDate[e.date]) byDate[e.date] = []; byDate[e.date].push(e); });
  const sortedDates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  // ── Chart data: last 12 months, 6 at a time ───────────────
  const now12 = new Date();
  const last12Months = [];
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now12.getFullYear(), now12.getMonth() - i, 1);
    last12Months.push({ year: d.getFullYear(), month: d.getMonth(), key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}` });
  }
  // chartStartIdx: which index in last12Months the 6-month window starts at
  // Ensure selected month is always visible — sync chartStartIdx to currentMonth
  const selectedKey = `${currentMonth.year}-${String(currentMonth.month+1).padStart(2,"0")}`;
  const selectedIdx = last12Months.findIndex(m => m.key === selectedKey);
  // Compute window: chartStartIdx clamped so selected month is always in view
  const clampedStart = Math.min(Math.max(chartStartIdx, 0), 6);
  const chartMonths = last12Months.slice(clampedStart, clampedStart + 6);

  const chartData = chartMonths.map(({ key, year, month }) => {
    const exps = allMonthlyData[key] || [];
    const filtered = chartFilter === "all" ? exps : exps.filter(e => e.category === chartFilter);
    return {
      label: SHORT_MONTHS[month],
      year, month, key,
      total: filtered.reduce((s, e) => s + e.amount, 0),
    };
  });
  const chartMax = Math.max(...chartData.map(d => d.total), 1);
  const barColor = chartFilter === "all" ? "#1a1714" : HOME_CAT_TEXT[chartFilter] || "#1a1714";

  // ── Load all data on mount ─────────────────────────────────
  React.useEffect(() => {
    if (Object.keys(allMonthlyData).length === 0) onLoadAllData();
  }, []); // eslint-disable-line

  // ── Handlers ──────────────────────────────────────────────
  async function handleAdd(exp) {
    const { data, error } = await supabase.from("monthly_expenses").insert({
      user_id: user.id, category: exp.category, description: exp.description || null,
      amount: exp.amount, date: exp.date, notes: exp.notes || null,
    }).select().single();
    if (error) { showToast("Could not save expense.", "error"); return; }
    const newExp = { id: data.id, ...exp };
    setMonthlyExpenses(prev => [newExp, ...prev].sort((a,b) => b.date.localeCompare(a.date)));
    // update allMonthlyData
    const key = exp.date.slice(0,7);
    setAllMonthlyData(prev => ({ ...prev, [key]: [...(prev[key]||[]), newExp] }));
    setShowAdd(false);
    showToast("Expense added!");
  }

  async function handleUpdate(id, updated) {
    const { error } = await supabase.from("monthly_expenses").update({
      category: updated.category, description: updated.description || null,
      amount: updated.amount, date: updated.date, notes: updated.notes || null,
    }).eq("id", id).eq("user_id", user.id);
    if (error) { showToast("Could not update expense.", "error"); return; }
    setMonthlyExpenses(prev => prev.map(e => e.id === id ? { ...e, ...updated } : e).sort((a,b) => b.date.localeCompare(a.date)));
    const key = updated.date.slice(0,7);
    setAllMonthlyData(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { next[k] = next[k].filter(e => e.id !== id); });
      next[key] = [...(next[key]||[]), { id, ...updated }];
      return next;
    });
    setEditingExp(null);
    showToast("Expense updated!");
  }

  async function handleDelete(id) {
    await supabase.from("monthly_expenses").delete().eq("id", id).eq("user_id", user.id);
    setMonthlyExpenses(prev => prev.filter(e => e.id !== id));
    setAllMonthlyData(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { next[k] = next[k].filter(e => e.id !== id); });
      return next;
    });
    setEditingExp(null);
    showToast("Expense deleted.");
  }

  function exportCSV() {
    const rows = [["Date","Category","Description","Amount","Notes"]];
    monthlyExpenses.forEach(e => {
      const cat = HOME_CATEGORIES.find(c => c.value === e.category)?.label || e.category;
      rows.push([e.date, cat, e.description || "", e.amount, e.notes || ""]);
    });
    const csv  = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href = url;
    a.download = `sejour-home-${currentMonth.year}-${String(currentMonth.month+1).padStart(2,"0")}.csv`;
    a.click(); URL.revokeObjectURL(url);
  }

  async function handleSyncSheets() {
    setSyncingSheets(true);
    showToast("Syncing to Google Sheets…");
    try {
      const res  = await fetch(EDGE_FN_URL + "/sync-monthly", { method:"POST", headers:EDGE_HEADERS, body: JSON.stringify({ userId:user.id, year:currentMonth.year, month:currentMonth.month, expenses:monthlyExpenses, homeCurrency:user.homeCurrency, budget:budget || null }) });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || "Sync failed");
      showToast("Synced to Google Sheets!");
    } catch(e) { showToast(e.message || "Sync failed", "error"); }
    setSyncingSheets(false);
  }

  // ── Topbar (shared) ───────────────────────────────────────
  const Topbar = ({ children }) => (
    <div style={S.topbar}>
      <div style={{ width:30, height:30, background:"#1a1714", borderRadius:"8px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <Plane size={16} color="#f5f0e8" />
      </div>
      <span style={{ fontWeight:700, fontSize:"15px", letterSpacing:"0.2em" }}>SÉJOUR</span>
      <div style={{ display:"flex", gap:"2px", background:"#f4f2ef", padding:"3px", borderRadius:"8px", marginLeft:"4px" }}>
        {["travel","home"].map(id => (
          <button key={id} onClick={() => switchMode(id)} style={{ padding:"5px 12px", borderRadius:"6px", border:"none", fontSize:"12px", fontWeight:500, cursor:"pointer", fontFamily:"inherit", background: mode===id ? "white" : "transparent", color: mode===id ? "#1a1714" : "#7a7060", boxShadow: mode===id ? "0 1px 3px rgba(0,0,0,0.1)" : "none", transition:"all .15s" }}>
            {id.charAt(0).toUpperCase() + id.slice(1)}
          </button>
        ))}
      </div>
      <div style={{ flex:1 }} />
      {children}
      <button style={S.btnGhost} onClick={onGoSettings} title="Settings"><Settings size={15} /></button>
      <button style={{ ...S.btnGhost, color:"#958b79" }} onClick={onLogout}><LogOut size={14} /></button>
    </div>
  );

  // ════════════════════════════════════════════════════════════
  // LANDING VIEW
  // ════════════════════════════════════════════════════════════
  if (homeView === "landing") {
    return (
      <div>
        <Topbar />
        <div style={{ maxWidth:860, margin:"0 auto", padding:"16px clamp(12px, 3vw, 20px) 40px" }}>

          {/* Welcome header */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"24px" }}>
            <div>
              <h1 style={{ fontSize:"clamp(22px, 6vw, 30px)", fontWeight:700, marginBottom:"4px" }}>Welcome home, {user.displayName}.</h1>
            </div>
            <button style={{ ...S.btnPrimary, width:"auto" }} onClick={() => setShowAdd(true)}><Plus size={15} /> Expense</button>
          </div>

          {/* ── Bar chart ── */}
          <div style={{ ...S.card, padding:"20px", marginBottom:"16px" }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"14px" }}>
              <h2 style={{ fontSize:"13px", fontWeight:700, display:"flex", alignItems:"center", gap:"6px" }}><BarChart3 size={13} /> Spending</h2>
              <div style={{ display:"flex", alignItems:"center", gap:"4px" }}>
                <button style={S.btnGhost} onClick={() => setChartStartIdx(i => Math.max(i-1, 0))} disabled={clampedStart === 0}><ChevronLeft size={16} /></button>
                <button style={S.btnGhost} onClick={() => setChartStartIdx(i => Math.min(i+1, 6))} disabled={clampedStart === 6}><ChevronRight size={16} /></button>
              </div>
            </div>

            {/* Category filter pills */}
            <div style={{ display:"flex", gap:"6px", flexWrap:"wrap", marginBottom:"16px" }}>
              <button onClick={() => setChartFilter("all")} style={{ padding:"4px 10px", borderRadius:"999px", border:"1.5px solid", fontSize:"11px", fontWeight:500, cursor:"pointer", fontFamily:"inherit", borderColor: chartFilter==="all" ? "#1a1714" : "#e5e0d8", background: chartFilter==="all" ? "#1a1714" : "white", color: chartFilter==="all" ? "#f5f0e8" : "#625a4d" }}>All</button>
              {HOME_CATEGORIES.map(c => (
                <button key={c.value} onClick={() => setChartFilter(c.value)} style={{ padding:"4px 10px", borderRadius:"999px", border:"1.5px solid", fontSize:"11px", fontWeight:500, cursor:"pointer", fontFamily:"inherit", borderColor: chartFilter===c.value ? HOME_CAT_TEXT[c.value] : "#e5e0d8", background: chartFilter===c.value ? HOME_CAT_COLORS[c.value] : "white", color: chartFilter===c.value ? HOME_CAT_TEXT[c.value] : "#625a4d" }}>{c.label}</button>
              ))}
            </div>

            {/* Bars */}
            <div style={{ display:"flex", alignItems:"flex-end", gap:"8px", height:"120px" }}>
              {chartData.map(d => {
                const pct = chartMax > 0 ? (d.total / chartMax) * 100 : 0;
                const isSelected = d.year === currentMonth.year && d.month === currentMonth.month;
                return (
                  <div key={d.key} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:"4px", cursor: isSelected ? "default" : "pointer" }} onClick={() => !isSelected && onChangeMonth(d.year, d.month)}>
                    <p style={{ fontSize:"10px", color:"#a39888", fontWeight:600 }}>{d.total > 0 ? Math.round(d.total) : ""}</p>
                    <div style={{ width:"100%", display:"flex", alignItems:"flex-end", height:"80px" }}>
                      <div style={{ width:"100%", height: pct > 0 ? `${pct}%` : "3px", minHeight:"3px", background: isSelected ? barColor : pct > 0 ? barColor + "99" : "#e5e0d8", borderRadius:"4px 4px 0 0", transition:"height .3s" }} />
                    </div>
                    <p style={{ fontSize:"11px", color: isSelected ? "#1a1714" : "#a39888", fontWeight: isSelected ? 700 : 400 }}>{d.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Month selector + summary ── */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"12px" }}>
            <div>
              <h1 style={{ fontSize:"clamp(20px, 5vw, 24px)", fontWeight:700, marginBottom:"2px" }}>{monthLabel}</h1>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:"4px" }}>
              <button style={S.btnGhost} onClick={() => {
                let m=currentMonth.month-1, y=currentMonth.year;
                if(m<0){m=11;y--;}
                onChangeMonth(y,m);
                // shift chart window left by 1 if needed
                const newKey = `${y}-${String(m+1).padStart(2,"0")}`;
                const newIdx = last12Months.findIndex(x => x.key === newKey);
                if (newIdx >= 0 && newIdx < clampedStart) setChartStartIdx(s => Math.max(s-1, 0));
                if (newIdx >= 0 && newIdx >= clampedStart + 6) setChartStartIdx(s => Math.min(s+1, 6));
              }}><ChevronLeft size={18} /></button>
              <button style={S.btnGhost} onClick={() => {
                let m=currentMonth.month+1, y=currentMonth.year;
                if(m>11){m=0;y++;}
                onChangeMonth(y,m);
                const newKey = `${y}-${String(m+1).padStart(2,"0")}`;
                const newIdx = last12Months.findIndex(x => x.key === newKey);
                if (newIdx >= 0 && newIdx >= clampedStart + 6) setChartStartIdx(s => Math.min(s+1, 6));
                if (newIdx >= 0 && newIdx < clampedStart) setChartStartIdx(s => Math.max(s-1, 0));
              }} disabled={isThisMonth}><ChevronRight size={18} /></button>
            </div>
          </div>

          {/* Summary card */}
          <div style={{ ...S.card, padding:"20px", marginBottom:"16px" }}>
            <div style={{ display:"grid", gridTemplateColumns: isThisMonth ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap:"12px", marginBottom: budget ? "16px" : "0" }}>
              <div>
                <p style={{ fontSize:"11px", color:"#a39888", marginBottom:"4px", textTransform:"uppercase", letterSpacing:"0.06em" }}>Spent</p>
                <p style={{ fontSize:"clamp(16px,4vw,20px)", fontWeight:700 }}>{fmt(total)}</p>
                <p style={{ fontSize:"11px", color:"#a39888" }}>{user.homeCurrency}</p>
              </div>
              {budget && (
                <div>
                  <p style={{ fontSize:"11px", color:"#a39888", marginBottom:"4px", textTransform:"uppercase", letterSpacing:"0.06em" }}>Budget</p>
                  <p style={{ fontSize:"clamp(16px,4vw,20px)", fontWeight:700 }}>{fmt(budget)}</p>
                  <p style={{ fontSize:"11px", color:"#a39888" }}>{user.homeCurrency}</p>
                </div>
              )}
              <div>
                <p style={{ fontSize:"11px", color:"#a39888", marginBottom:"4px", textTransform:"uppercase", letterSpacing:"0.06em" }}>Daily Avg</p>
                <p style={{ fontSize:"clamp(16px,4vw,20px)", fontWeight:700 }}>{fmt(dailyAvg)}</p>
                <p style={{ fontSize:"11px", color:"#a39888" }}>{user.homeCurrency}</p>
              </div>
              {isThisMonth && (
                <div>
                  <p style={{ fontSize:"11px", color:"#a39888", marginBottom:"4px", textTransform:"uppercase", letterSpacing:"0.06em" }}>Days Left</p>
                  <p style={{ fontSize:"clamp(16px,4vw,20px)", fontWeight:700 }}>{daysLeft}</p>
                  <p style={{ fontSize:"11px", color:"#a39888" }}>of {daysInMonth}</p>
                </div>
              )}
            </div>
            {budget && (
              <div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:"11px", color:"#a39888", marginBottom:"6px" }}>
                  <span>{budgetOver ? "Over budget by " + user.homeCurrency + " " + fmt(total - budget) : user.homeCurrency + " " + fmt(budget - total) + " remaining"}</span>
                  <span>{budgetPct?.toFixed(0)}%</span>
                </div>
                <div style={{ height:"6px", background:"#f0ede8", borderRadius:"999px", overflow:"hidden" }}>
                  <div style={{ height:"100%", width:budgetPct + "%", background: budgetOver ? "#dc2626" : "#1a1714", borderRadius:"999px", transition:"width .4s" }} />
                </div>
              </div>
            )}
          </div>



          {/* View expenses button */}
          <button style={{ ...S.btnPrimary, width:"100%", justifyContent:"center", padding:"13px", marginTop:"16px" }} onClick={() => setHomeView("detail")}>
            View expenses →
          </button>
        </div>

        {showAdd && <HomeExpenseModal onClose={() => setShowAdd(false)} onSave={handleAdd} homeCurrency={user.homeCurrency} />}
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ════════════════════════════════════════════════════════════
  return (
    <div>
      <div style={S.topbar} className="topbar-mobile-gap">
        <button style={{ ...S.btnGhost, padding:"5px 8px" }} onClick={() => setHomeView("landing")}><ArrowLeft size={18} /></button>
        <div style={{ width:32, height:32, background:"#f0ede8", borderRadius:"8px", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Home size={16} color="#625a4d" />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontWeight:700, fontSize:"15px", margin:0, lineHeight:1.2 }}>{monthLabel}</p>
          <p style={{ fontSize:"11px", color:"#7a7060", margin:0, lineHeight:1.2 }}>Home expenses</p>
        </div>
        {monthlyExpenses.length > 0 && (
          <button style={{ ...S.btnSecondary, fontSize:"12px", padding:"7px 8px" }} onClick={exportCSV} title="Export CSV" className="topbar-btn-sm">
            <Download size={13} />
            <span className="desktop-only">Export CSV</span>
          </button>
        )}
        {user.hasGoogle && monthlyExpenses.length > 0 && (
          <button style={{ ...S.btnSecondary, fontSize:"12px", padding:"7px 8px" }} onClick={handleSyncSheets} disabled={syncingSheets} className="topbar-btn-sm">
            {syncingSheets ? <Loader2 size={13} style={{ animation:"spin 1s linear infinite" }} /> : <BarChart3 size={13} />}
            <span className="desktop-only">{syncingSheets ? "Syncing…" : "Sync Sheets"}</span>
          </button>
        )}
        <button style={{ ...S.btnPrimary, width:"auto", padding:"8px 14px", fontSize:"13px" }} onClick={() => setShowAdd(true)}>
          <Plus size={14} /> Add
        </button>
        <button style={S.btnGhost} onClick={onGoSettings} title="Settings"><Settings size={15} /></button>
      </div>

      <div style={{ maxWidth:860, margin:"0 auto", padding:"12px clamp(10px, 3vw, 16px)", overflowX:"hidden", width:"100%", boxSizing:"border-box" }}>

        {/* Stats grid — matches trip layout */}
        <div className="stats-grid" style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"10px", marginBottom:"14px", width:"100%" }}>
          {[
            { Icon: Calendar,   label: isThisMonth ? "Days Left" : "Days",    val: isThisMonth ? daysLeft + " of " + daysInMonth : daysInMonth + " days",  sub: MONTH_NAMES[currentMonth.month] + " " + currentMonth.year },
            { Icon: DollarSign, label: "Total Spent", val: fmt(total),         sub: monthlyExpenses.length + " expense" + (monthlyExpenses.length !== 1 ? "s" : "") },
            { Icon: TrendingUp, label: "Per Day",     val: fmt(dailyAvg),      sub: "daily avg" },
          ].map(s => (
            <div key={s.label} style={{ ...S.card, padding:"14px 16px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"5px", color:"#a39888", fontSize:"11px", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:"6px" }}><s.Icon size={11} /> {s.label}</div>
              <p style={{ fontWeight:700, fontSize:"clamp(13px, 4vw, 18px)", marginBottom:"2px", wordBreak:"break-word" }}>{s.val}</p>
              <p style={{ fontSize:"11px", color:"#a39888" }}>{s.sub}</p>
            </div>
          ))}
        </div>

        {/* Budget bar */}
        {budget && (
          <div style={{ ...S.card, padding:"14px 16px", marginBottom:"14px" }}>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:"12px", color:"#7a7060", marginBottom:"7px" }}>
              <span style={{ display:"flex", alignItems:"center", gap:"4px" }}><Landmark size={12} /> Budget: {user.homeCurrency} {fmt(budget)}</span>
              <span style={{ fontWeight:600 }}>{budgetPct?.toFixed(1)}% used</span>
            </div>
            <div style={{ height:"7px", background:"#e5e0d8", borderRadius:"999px", overflow:"hidden" }}>
              <div style={{ height:"100%", width:(budgetPct||0)+"%", background: (budgetPct||0)>90?"#ef4444":(budgetPct||0)>70?"#eab308":"#22c55e", borderRadius:"999px", transition:"width .6s" }} />
            </div>
            <p style={{ fontSize:"11px", color: budgetOver?"#dc2626":"#a39888", marginTop:"5px", textAlign:"right" }}>
              {budgetOver ? fmt(total - budget) + " over budget" : fmt(budget - total) + " remaining"}
            </p>
          </div>
        )}

        {/* Expense list — matches trip grouped layout */}
        <div className="trip-layout" style={{ display:"grid", gap:"16px", alignItems:"start", width:"100%" }}>
          <div style={{ minWidth:0, width:"100%" }}>
            {monthlyExpenses.length === 0 ? (
              <div style={{ ...S.card, padding:"48px", textAlign:"center" }}>
                <div style={{ width:52, height:52, background:"#f0f0ee", borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 14px" }}><Wallet size={24} color="#a39888" /></div>
                <h3 style={{ fontWeight:600, marginBottom:"6px" }}>No expenses yet</h3>
                <p style={{ color:"#7a7060", fontSize:"13px" }}>Use the Add button above to log your first expense.</p>
              </div>
            ) : (
              sortedDates.map(date => (
                <div key={date} style={{ marginBottom:"20px" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
                    <p style={{ fontSize:"11px", fontWeight:600, color:"#7a7060", textTransform:"uppercase", letterSpacing:"0.06em", whiteSpace:"nowrap", margin:0 }}>
                      {new Date(date + "T00:00:00").toLocaleDateString("en", { weekday:"short", month:"short", day:"numeric" })}
                    </p>
                    <div style={{ flex:1, height:"1px", background:"#e5e0d8" }} />
                    <p style={{ fontSize:"11px", fontFamily:"monospace", color:"#a39888", whiteSpace:"nowrap", margin:0 }}>
                      {user.homeCurrency} {fmt(byDate[date].reduce((s, e) => s + e.amount, 0))}
                    </p>
                  </div>
                  <div style={{ ...S.card, overflow:"hidden" }}>
                    {byDate[date].map((exp, i) => {
                      const cat     = HOME_CATEGORIES.find(c => c.value === exp.category) || HOME_CATEGORIES[HOME_CATEGORIES.length-1];
                      const CatIcon = cat.Icon;
                      return (
                        <div key={exp.id} onClick={() => setEditingExp(exp)}
                          style={{ display:"flex", alignItems:"center", gap:"12px", padding:"10px 16px", borderTop: i > 0 ? "1px solid #f0ede8" : "none", cursor:"pointer" }}>
                          <div style={{ width:34, height:34, borderRadius:"9px", background:HOME_CAT_COLORS[exp.category]||"#f0ede8", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                            <CatIcon size={15} color={HOME_CAT_TEXT[exp.category]||"#625a4d"} />
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ fontSize:"14px", fontWeight:500, margin:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{exp.description || cat.label}</p>
                            <p style={{ fontSize:"11px", color:"#a39888", margin:0 }}>{cat.label}</p>
                          </div>
                          <p style={{ fontSize:"14px", fontWeight:600, flexShrink:0 }}>{user.homeCurrency} {fmt(exp.amount)}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Breakdown sidebar — matches trip */}
          <div style={{ display:"flex", flexDirection:"column", gap:"12px", position:"sticky", top:"68px", minWidth:0 }}>
            {catTotals.length > 0 && (
              <HomeBreakdownCard catTotals={catTotals} total={total} homeCurrency={user.homeCurrency} />
            )}
          </div>
        </div>
      </div>

      {showAdd    && <HomeExpenseModal onClose={() => setShowAdd(false)}    onSave={handleAdd}                            homeCurrency={user.homeCurrency} />}
      {editingExp && <HomeExpenseModal onClose={() => setEditingExp(null)} onSave={u => handleUpdate(editingExp.id, u)} onDelete={() => handleDelete(editingExp.id)} existing={editingExp} homeCurrency={user.homeCurrency} />}
    </div>
  );
}


function HomeBreakdownCard({ catTotals, total, homeCurrency }) {
  const [view, setView] = useState("bars");
  const PIE_SIZE = 160;
  const cx = PIE_SIZE / 2, cy = PIE_SIZE / 2, r = 62, innerR = 36;

  function polarToXY(angleDeg, radius) {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  }

  function buildSlices() {
    let startAngle = 0;
    return catTotals.map(c => {
      const pct = total > 0 ? c.total / total : 0;
      const sweep = pct * 360;
      const endAngle = startAngle + sweep;
      const largeArc = sweep > 180 ? 1 : 0;
      const p1 = polarToXY(startAngle, r), p2 = polarToXY(endAngle, r);
      const i1 = polarToXY(startAngle, innerR), i2 = polarToXY(endAngle, innerR);
      const path = sweep < 0.5 ? null : `M ${p1.x} ${p1.y} A ${r} ${r} 0 ${largeArc} 1 ${p2.x} ${p2.y} L ${i2.x} ${i2.y} A ${innerR} ${innerR} 0 ${largeArc} 0 ${i1.x} ${i1.y} Z`;
      const result = { ...c, path, pct, startAngle, endAngle };
      startAngle = endAngle;
      return result;
    });
  }

  const slices = buildSlices();
  function fmt(n) { return (n||0).toLocaleString(undefined, { minimumFractionDigits:2, maximumFractionDigits:2 }); }

  return (
    <div style={{ ...S.card, padding:"16px" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"14px" }}>
        <h3 style={{ fontWeight:700, fontSize:"13px", display:"flex", alignItems:"center", gap:"5px" }}><BarChart3 size={13} /> Breakdown ({homeCurrency})</h3>
        <div style={{ display:"flex", gap:"3px", background:"#f4f2ef", padding:"3px", borderRadius:"8px" }}>
          {[["bars", <BarChart3 size={11} />], ["pie", <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2a10 10 0 0 1 10 10h-10z"/><circle cx="12" cy="12" r="7" fill="none" stroke="currentColor" strokeWidth="2"/></svg>]].map(([v, icon]) => (
            <button key={v} onClick={() => setView(v)}
              style={{ padding:"4px 8px", borderRadius:"5px", border:"none", fontSize:"11px", cursor:"pointer", fontFamily:"inherit", fontWeight:500, background: view===v ? "white" : "transparent", color: view===v ? "#1a1714" : "#a39888", boxShadow: view===v ? "0 1px 3px rgba(0,0,0,0.1)" : "none" }}>
              {icon}
            </button>
          ))}
        </div>
      </div>
      {view === "bars" ? (
        <div>
          {catTotals.map(c => {
            const CatIcon = c.Icon;
            const pct = total > 0 ? (c.total / total) * 100 : 0;
            return (
              <div key={c.value} style={{ marginBottom:"10px" }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:"12px", marginBottom:"4px", alignItems:"center" }}>
                  <span style={{ display:"flex", alignItems:"center", gap:"5px" }}>
                    <div style={{ width:20, height:20, borderRadius:"5px", background:HOME_CAT_COLORS[c.value], display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <CatIcon size={11} color={HOME_CAT_TEXT[c.value]} />
                    </div>
                    {c.label}
                  </span>
                  <span style={{ fontFamily:"monospace", fontWeight:600 }}>{homeCurrency} {fmt(c.total)}</span>
                </div>
                <div style={{ height:"3px", background:"#e5e0d8", borderRadius:"999px", overflow:"hidden" }}>
                  <div style={{ height:"100%", width:pct+"%", background:HOME_CAT_TEXT[c.value]||"#a39888", borderRadius:"999px", transition:"width .4s" }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          <svg width="100%" viewBox={`0 0 ${PIE_SIZE} ${PIE_SIZE}`} style={{ display:"block", margin:"0 auto 12px" }}>
            {slices.map(s => s.path && <path key={s.value} d={s.path} fill={HOME_CAT_TEXT[s.value] || "#a39888"} stroke="white" strokeWidth="1.5" />)}
            <text x={cx} y={cy - 4} textAnchor="middle" fontSize="10" fill="#7a7060" fontFamily="inherit">Total</text>
            <text x={cx} y={cy + 10} textAnchor="middle" fontSize="9" fill="#1a1714" fontFamily="monospace" fontWeight="bold">{homeCurrency} {fmt(total)}</text>
          </svg>
          <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
            {slices.map(s => (
              <div key={s.value} style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:"11px" }}>
                <span style={{ display:"flex", alignItems:"center", gap:"5px" }}>
                  <span style={{ width:9, height:9, borderRadius:"2px", background: HOME_CAT_TEXT[s.value] || "#a39888", display:"inline-block", flexShrink:0 }} />{s.label}
                </span>
                <span style={{ fontFamily:"monospace", color:"#625a4d" }}>{(s.pct * 100).toFixed(0)}% · {homeCurrency} {fmt(s.total)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HomeExpenseModal({ onClose, onSave, onDelete, existing, homeCurrency }) {
  const today = new Date().toISOString().split("T")[0];
  const [form,    setForm]    = useState(existing ? { ...existing } : { category:"other", description:"", amount:"", date:today, notes:"" });
  const [saving,  setSaving]  = useState(false);
  const [confirm, setConfirm] = useState(false);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSave() {
    if (!form.amount || isNaN(parseFloat(form.amount))) return;
    setSaving(true);
    await onSave({ ...form, amount: parseFloat(form.amount) });
    setSaving(false);
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div className="modal-sheet" style={{ background:"white", width:"100%", maxWidth:"480px", borderRadius:"14px", overflow:"hidden", maxHeight:"90vh", display:"flex", flexDirection:"column" }} onClick={e => e.stopPropagation()}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 20px", borderBottom:"1px solid #f0ede8" }}>
          <h2 style={{ fontSize:"16px", fontWeight:700, margin:0 }}>{existing ? "Edit Expense" : "Add Expense"}</h2>
          <button style={S.btnGhost} type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ overflowY:"auto", padding:"20px", display:"flex", flexDirection:"column", gap:"16px" }}>
          {/* Category */}
          <div>
            <label style={S.label}>Category</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(5, 1fr)", gap:"6px" }}>
              {HOME_CATEGORIES.map(c => {
                const sel = form.category === c.value;
                const CI  = c.Icon;
                return (
                  <button key={c.value} type="button" onClick={() => set("category", c.value)}
                    style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:"4px", height:"56px", borderRadius:"10px", border:"2px solid", borderColor: sel ? "#1a1714" : HOME_CAT_TEXT[c.value], background: sel ? "#1a1714" : HOME_CAT_COLORS[c.value], color: sel ? "#f5f0e8" : HOME_CAT_TEXT[c.value], cursor:"pointer", fontSize:"9px", fontFamily:"inherit", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.03em", boxSizing:"border-box", width:"100%", minWidth:0 }}>
                    <CI size={15} color={sel ? "#f5f0e8" : HOME_CAT_TEXT[c.value]} />{c.label}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Description */}
          <div>
            <label style={S.label}>Description</label>
            <input style={S.input} value={form.description} onChange={e => set("description", e.target.value)} />
          </div>
          {/* Amount */}
          <div>
            <label style={S.label}>Amount</label>
            <div style={{ position:"relative" }}>
              <span style={{ position:"absolute", left:"12px", top:"50%", transform:"translateY(-50%)", fontSize:"13px", color:"#7a7060", fontWeight:500 }}>{homeCurrency}</span>
              <input style={{ ...S.input, paddingLeft:"48px" }} type="number" min="0" step="0.01" value={form.amount} onChange={e => set("amount", e.target.value)} />
            </div>
          </div>
          {/* Date */}
          <div>
            <label style={S.label}>Date</label>
            <input style={S.input} type="date" value={form.date} onChange={e => set("date", e.target.value)} />
          </div>
          {/* Notes */}
          <div>
            <label style={S.label}>Notes <span style={{ color:"#a39888", fontWeight:400 }}>(optional)</span></label>
            <textarea style={{ ...S.input, height:"72px", resize:"none", fontFamily:"inherit", lineHeight:1.5 }} value={form.notes} onChange={e => set("notes", e.target.value)} />
          </div>
        </div>
        <div style={{ padding:"16px 20px", borderTop:"1px solid #f0ede8", display:"flex", gap:"8px" }}>
          {existing && !confirm && (
            <button type="button" style={{ ...S.btnGhost, color:"#dc2626" }} onClick={() => setConfirm(true)}><Trash2 size={14} /></button>
          )}
          {confirm && (
            <button type="button" style={{ ...S.btnGhost, color:"#dc2626", fontSize:"12px" }} onClick={onDelete}>Confirm delete</button>
          )}
          <div style={{ flex:1 }} />
          <button type="button" style={S.btnSecondary} onClick={onClose}>Cancel</button>
          <button type="button" style={S.btnPrimary} onClick={handleSave} disabled={saving || !form.amount}>
            {saving ? <Loader2 size={14} style={{ animation:"spin 1s linear infinite" }} /> : null}
            {saving ? "Saving…" : existing ? "Save" : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
}
